// src/common/footnotes.js
import { pickBase, safeFetchJson, uint8ToB64, getClientId } from "./api.js";
import { renderFootnoteCards, createFootnoteCard, renderPages, highlightFootnoteInList, setCardPending, 
  renderAuditResults, showFilePickerDialog, updateCitationUI, renderInitialCitationPlaceholders,
  ensureVerifyAllOverlay } from "./ui.js";
import { showInlineAsk, showResolveDialog, clearInlineAsksForCard } from './ask.js';
import { extractErrorMessage } from './utils/error-utils.js';
import { isUnresolvedCrossRefTarget, pickRichestCitation, resolutionKeyFromCitation } from './utils/citation-utils.js';
import { FileCatalogManager } from './file-catalog.js';
import { ResultsCache } from './results-cache.js';
import { verifyFootnoteWorkflow } from './verification/workflow.js';
import { parseFootnote } from './verification/parse-stage.js';
import { resolveCitation } from './verification/resolve-stage.js';
import { analyzeCitation, analyzeJointGroup } from './verification/analyze-stage.js';
import { createBatchVerificationQueue } from './verification/batch-verify.js';
import { generateHtmlReport, generatePdfReport } from './report-generator.js';
import { getCitationTypeFilterManager, ensureCitationTypeFilterPanel } from './ui/citation-type-filter.js';
import {
  citationMatchesStatusFilter, getStatusFilter, setStatusFilter, setStatusFilterApplier,
} from './ui/status-filter.js';
import { getSettingsForApiPayload } from './settings.js';
import { formatCitationType } from './utils/type-formatter.js';

const pdfParseInflightByDocument = new Map();
const SOURCE_DELETE_ARM_MS = 8000;

export class FootnotesManager {
  /**
   * @param {Object} platform  adapter with: getDocxBase64(), pickFile(), getFileBytes?(), setStatus?(text)
   */
  constructor(apiBase = null, clientId = null, platform = null, getAccessTokenFn = null) {
    this.apiBase = apiBase;
    this.clientId = clientId;
    this.platform = platform;
    this.getAccessTokenFn = getAccessTokenFn; // Function to retrieve current access token
    this.items = [];
    this.footnotesCache = []; // Initialize footnotesCache
    this.headings = [];
    this.pages = [];
    this.pollers = new Map();
    this.inflight = new Set();
    this.currentDocumentId = null;
    this.state = {
      documentId: null,
      documentPath: null,
      fileBytes: null,
    };
    this._pdfHighlightCache = new Map();
    this._pdfHighlightRequestSeq = 0;
    this._citationResetArms = new Map();
    
    // Initialize file catalog manager
    this.fileCatalogManager = new FileCatalogManager();
    
    // Initialize results cache
    this.resultsCache = new ResultsCache();
    
    // Store in-progress citations for verifyAll (so ask_user can look them up)
    this._inProgressCitations = new Map(); // footnoteId -> [citations]
    
    // Initialize citation type filter manager
    this.citationTypeFilter = getCitationTypeFilterManager();
    
    // Set up filter callbacks
    this.citationTypeFilter.setCallbacks({
      onFilterChange: (change) => this._onFilterChange(change),
      onTypesUpdated: (stats) => this._onTypesUpdated(stats)
    });
    // Status filter (clickable counts in the document stats bar) shares the visibility pass
    setStatusFilterApplier(() => this._applyVisibilityFilter());

    // Detect if we're in web-app mode
    this.isWebApp = typeof window !== "undefined" && 
                    window.footcheckAuth?.getAccessToken &&
                    (window.location?.pathname?.includes('web-app') || document.title?.includes('Web'));
  }

  /**
   * Initialize the citation type filter UI panel
   * @param {string} containerId - ID of the container element (e.g., 'fn-pane')
   */
  initializeCitationTypeFilter(containerId = 'fn-pane') {
    ensureCitationTypeFilterPanel(containerId);
    console.log('[FootnotesManager] Citation type filter panel initialized');
  }

  /**
   * Register citation types from parsed citations
   * @param {Array} citations - Array of parsed citation objects
   */
  registerCitationTypes(citations) {
    console.log('[FootnotesManager] registerCitationTypes called with', citations?.length || 0, 'citations');
    if (!citations || !Array.isArray(citations)) {
      console.log('[FootnotesManager] No citations to register');
      return;
    }
    
    // Ensure the filter panel exists (lazy initialization)
    if (!this.citationTypeFilter.containerEl) {
      console.log('[FootnotesManager] Filter panel not yet mounted, attempting to create...');
      // Try common container IDs
      for (const containerId of ['fn-pane', 'app', 'footnote-list']) {
        const container = document.getElementById(containerId);
        if (container) {
          console.log('[FootnotesManager] Found container:', containerId);
          ensureCitationTypeFilterPanel(containerId);
          break;
        }
      }
    }
    
    this.citationTypeFilter.registerCitations(citations);
  }

  /**
   * Check if a citation should be shown based on type filters
   * @param {Object} citation - Citation object with source.type
   * @returns {boolean}
   */
  shouldShowCitation(citation) {
    if (!citation?.source?.type) return true;
    return this.citationTypeFilter.shouldShow(citation.source.type);
  }

  /**
   * Check if a citation should be verified based on type filters
   * @param {Object} citation - Citation object with source.type
   * @returns {boolean}
   */
  shouldVerifyCitation(citation) {
    if (!citation?.source?.type) return true;
    return this.citationTypeFilter.shouldVerify(citation.source.type);
  }

  /**
   * Get excluded type keys for verification
   * @returns {string[]}
   */
  getExcludedVerifyTypes() {
    return this.citationTypeFilter.getExcludedVerifyTypes();
  }

  /**
   * Get hidden type keys for display
   * @returns {string[]}
   */
  getHiddenTypes() {
    return this.citationTypeFilter.getHiddenTypes();
  }

  /**
   * Best-effort read of the current "Verify" filter state from the DOM.
   * This is intentionally used as a source of truth for Desktop App / Word Add-in,
   * because bundling/hot-reload can sometimes result in multiple JS singletons.
   *
   * @param {string} typeKey - normalized type key (e.g. "Other > website")
   * @returns {boolean|null} true/false if determinable from DOM, otherwise null
   */
  _domShouldVerifyTypeKey(typeKey) {
    try {
      if (!typeKey || typeKey === '—') return true;
      const normalize = (val) => String(val || '').trim().toLowerCase();
      const targetType = normalize(typeKey);
      const categoryKey = String(typeKey).split(' > ')[0] || null;
      const targetCategory = normalize(categoryKey);

      // Prefer the footnotes pane filter panel if available (Desktop App)
      const candidates = [];
      const fnPane = document.getElementById('fn-pane');
      if (fnPane) {
        candidates.push(...Array.from(fnPane.querySelectorAll('.citation-type-filter-panel')));
      }
      // Fallback: any filter panels in the document (Word add-in / other layouts)
      candidates.push(...Array.from(document.querySelectorAll('.citation-type-filter-panel')));

      const uniqPanels = Array.from(new Set(candidates)).filter(Boolean);
      if (uniqPanels.length === 0) return null;

      const findCheckboxes = (panelEl) => {
        // Find subtype row by normalized match (more robust than CSS selector exact-match)
        let subtypeVerifyCheckbox = null;
        const subtypeRows = panelEl.querySelectorAll('.subtype-row[data-type]');
        for (const row of subtypeRows) {
          if (normalize(row.dataset.type) === targetType) {
            subtypeVerifyCheckbox = row.querySelector('input.verify-checkbox');
            break;
          }
        }

        let categoryVerifyCheckbox = null;
        const categoryRows = panelEl.querySelectorAll('.category-row[data-category]');
        for (const row of categoryRows) {
          if (normalize(row.dataset.category) === targetCategory) {
            categoryVerifyCheckbox = row.querySelector('input.verify-checkbox');
            break;
          }
        }

        return { subtypeVerifyCheckbox, categoryVerifyCheckbox };
      };

      // Prefer a panel that has a matching subtype row, else a matching category row
      let chosen = null;
      for (const panelEl of uniqPanels) {
        const { subtypeVerifyCheckbox } = findCheckboxes(panelEl);
        if (subtypeVerifyCheckbox) {
          chosen = panelEl;
          break;
        }
      }
      if (!chosen && categoryKey) {
        for (const panelEl of uniqPanels) {
          const { categoryVerifyCheckbox } = findCheckboxes(panelEl);
          if (categoryVerifyCheckbox) {
            chosen = panelEl;
            break;
          }
        }
      }
      if (!chosen) {
        // We have panels, but none match this typeKey; don't guess.
        return null;
      }

      const { subtypeVerifyCheckbox, categoryVerifyCheckbox } = findCheckboxes(chosen);
      if (categoryVerifyCheckbox && categoryVerifyCheckbox.checked === false) {
        return false;
      }

      if (subtypeVerifyCheckbox) {
        return !!subtypeVerifyCheckbox.checked;
      }

      // Unknown subtype: defer to category checkbox if present; otherwise "unknown => include"
      if (categoryVerifyCheckbox) return !!categoryVerifyCheckbox.checked;
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Reset citation type filters (e.g., when loading a new document)
   */
  resetCitationTypeFilters() {
    this.citationTypeFilter.reset();
  }

  /**
   * Rebuild type counts from the current document while preserving the user's
   * show/verify choices. Reopened web documents receive parsed citations from
   * the verification cache after setFootnotes(), so the initial registration
   * pass cannot see them.
   */
  refreshCitationTypes() {
    this.citationTypeFilter.reset();
    const footnotes = this.footnotesCache?.length ? this.footnotesCache : (this.items || []);
    for (const footnote of footnotes) {
      const citations = footnote?.parsed_citations || footnote?.cached_citations;
      if (Array.isArray(citations)) {
        this.registerCitationTypes(citations);
      }
    }
  }

  /** Apply the current Show filters after cards or result blocks are rendered. */
  applyCitationTypeVisibility() {
    this._applyVisibilityFilter();
  }

  /**
   * Handle filter change events
   * @param {Object} change - { type: 'show'|'verify', typeKey, value }
   */
  _onFilterChange(change) {
    console.log('[FootnotesManager] _onFilterChange called:', change);
    
    if (change.type === 'show') {
      console.log('[FootnotesManager] Applying visibility filter...');
      // Re-render footnote cards with visibility filter
      this._applyVisibilityFilter();
    }
  }

  /**
   * Handle types updated events
   * @param {Object} stats - { types, totalCount, visibleCount, verifyCount }
   */
  _onTypesUpdated(stats) {
    console.log('[FootnotesManager] Citation types updated:', stats.subtypes?.size || 0, 'subtypes', stats.totalCount, 'total');
    // Apply visibility filter after types are updated (e.g., after inline citation parsing)
    // Use setTimeout to ensure DOM has been updated with new cards after rendering completes
    setTimeout(() => {
      this._applyVisibilityFilter();
    }, 0);
  }

  /**
   * Apply visibility filter to footnote cards
   */
  _applyVisibilityFilter() {
    console.log('[FootnotesManager] Applying visibility filter');
    
    // Try multiple container IDs
    let footnoteListEl = document.getElementById('footnote-list') || 
                         document.getElementById('app') ||
                         document.querySelector('.card-list');
    if (!footnoteListEl) {
      console.warn('[FootnotesManager] No footnote list container found');
      return;
    }

    // Find all footnote cards
    const cards = footnoteListEl.querySelectorAll('.card');
    console.log('[FootnotesManager] Found', cards.length, 'cards to filter');
    const statusFilter = getStatusFilter();

    cards.forEach(card => {
      const footnoteId = card.dataset.id;
      const footnote = this.footnotesCache?.find(f => String(f.id) === String(footnoteId)) ||
                       this.items?.find(f => String(f.id) === String(footnoteId));

      if (!footnote) {
        // Unknown card: only the status filter can say anything about it.
        if (statusFilter) card.style.display = 'none';
        return;
      }

      if (statusFilter) {
        this._applyStatusFilterToCard(card, footnote);
        return;
      }

      // Check parsed_citations
      const citations = footnote.parsed_citations || footnote.cached_citations || [];
      
      // Check if all citations in this card should be hidden
      let hasVisibleCitation = false;
      
      for (const citation of citations) {
        if (citation?.source?.type) {
          if (this.citationTypeFilter.shouldShow(citation.source.type)) {
            hasVisibleCitation = true;
            break;
          }
        } else {
          // No type = show by default
          hasVisibleCitation = true;
          break;
        }
      }
      
      // Also check inline_citation
      if (!hasVisibleCitation && footnote.inline_citation?.source?.type) {
        if (this.citationTypeFilter.shouldShow(footnote.inline_citation.source.type)) {
          hasVisibleCitation = true;
        }
      }
      
      // If no citations parsed yet, show the card
      if (citations.length === 0 && !footnote.inline_citation) {
        hasVisibleCitation = true;
      }
      
      // Show/hide the card
      card.style.display = hasVisibleCitation ? '' : 'none';
      
      // Also filter individual citation blocks within the card
      const citationBlocks = card.querySelectorAll('.citation-block');
      citationBlocks.forEach(block => {
        const citeId = block.dataset.citeId;
        const citation = citations.find(c => String(c.id) === String(citeId));
        // Untyped blocks are always reset, so clearing a status filter restores them.
        const shouldShow = !citation?.source?.type
          || this.citationTypeFilter.shouldShow(citation.source.type);
        block.style.display = shouldShow ? '' : 'none';
      });
    });

    this._hideEmptyHeadingGroups(footnoteListEl);
  }

  /**
   * Status filter pass for one card: a citation block stays visible only if it has a
   * result matching the active status AND its type is shown; the card stays visible
   * if any block does.
   */
  _applyStatusFilterToCard(card, footnote) {
    const results = this.resultsCache.getResults(card.dataset.id) || {};
    const jointGroups = this.resultsCache.getJointResults(card.dataset.id);
    const citations = [
      ...(footnote.parsed_citations || footnote.cached_citations || []),
      ...(footnote.inline_citation ? [footnote.inline_citation] : []),
    ];
    const typeShown = (citeId) => {
      const type = citations.find(c => String(c?.id) === String(citeId))?.source?.type;
      return !type || this.citationTypeFilter.shouldShow(type);
    };
    const matches = (citeId) => typeShown(citeId)
      && citationMatchesStatusFilter(results[citeId], jointGroups);

    let anyVisible = false;
    const blocks = card.querySelectorAll('.citation-block');
    blocks.forEach(block => {
      const visible = matches(block.dataset.citeId);
      block.style.display = visible ? '' : 'none';
      if (visible) anyVisible = true;
    });
    // Cards whose results have not been rendered into blocks yet
    if (blocks.length === 0) {
      anyVisible = Object.keys(results).some(citeId => citeId !== '__error__' && matches(citeId));
    }
    card.style.display = anyVisible ? '' : 'none';
  }

  /** Hide heading groups whose cards are all filtered out (deepest groups first). */
  _hideEmptyHeadingGroups(container) {
    const headers = Array.from(container.querySelectorAll('.footnote-group-header')).reverse();
    for (const headerEl of headers) {
      const contentEl = headerEl.nextElementSibling;
      if (!contentEl?.classList.contains('footnote-group-content')) continue;
      const cards = contentEl.querySelectorAll('.card');
      const empty = cards.length > 0
        && Array.from(cards).every(card => card.style.display === 'none');
      headerEl.classList.toggle('is-filtered-out', empty);
      contentEl.classList.toggle('is-filtered-out', empty);
    }
  }
  
  /**
   * Show all footnote cards (reset visibility)
   */
  _showAllCards() {
    const footnoteListEl = document.getElementById('footnote-list') || 
                           document.getElementById('app') ||
                           document.querySelector('.card-list');
    if (!footnoteListEl) return;
    
    const cards = footnoteListEl.querySelectorAll('.card');
    cards.forEach(card => {
      card.style.display = '';
      const citationBlocks = card.querySelectorAll('.citation-block');
      citationBlocks.forEach(block => {
        block.style.display = '';
      });
    });
  }

  // Setters
  setDocumentId(documentId) {
    if (this.currentDocumentId !== documentId) {
      this.resultsCache.clear();
      this.fileCatalogManager.clear();
      this.resetCitationTypeFilters();
      setStatusFilter(null);
    }
    this.currentDocumentId = documentId;
    this.fileCatalogManager.setCurrentDocumentId(documentId);
  }

  setApiBase(apiBase) {
    this.apiBase = apiBase;
    this.fileCatalogManager.setApiBase(apiBase);
  }

  setGetAccessTokenFn(fn) {
    this.getAccessTokenFn = fn;
    this.fileCatalogManager.setGetAuthHeadersFn(() => this.getAuthHeaders());
  }

  async getAuthHeaders(options = {}) {
    if (!this.getAccessTokenFn) {
      return {}; // No auth in dev mode or Word add-in (uses local /session)
    }
    try {
      const token = await this.getAccessTokenFn(options);
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.warn('[FootnotesManager] Failed to get access token:', error);
      return {};
    }
  }

  setClientId(clientId) {
    this.clientId = clientId;
    this.fileCatalogManager.setClientId(clientId);
  }

  setPlatform(platform) {
    this.platform = platform;
    this.fileCatalogManager.setPlatform(platform);
  }

  setDatabaseManager(databaseManager) {
    this.databaseManager = databaseManager;
  }


  setSseReady() {
    if (this.sseReadyResolve) {
      this.sseReadyResolve();
      this.sseReadyResolve = null;
    }
  }
  
  setContainers({ footnoteListEl }) {
    this.footnoteListEl = footnoteListEl;
    // Update results cache with footnote list element
    this.resultsCache.setFootnoteListEl(footnoteListEl);
    // Update results cache with file catalog manager
    this.resultsCache.setFileCatalogManager(this.fileCatalogManager);
    // Set up reassign citation file function
    this.resultsCache.setReassignCitationFileFn((footnoteId, citeId) => {
      return this.reassignCitationFile(footnoteId, citeId);
    });
    this.resultsCache.setResetCitationFn((footnoteId, citeId, interaction) => {
      return this.resetCitationResult(footnoteId, citeId, interaction);
    });
  }

  setFootnotes(footnotes) {
    this.items = footnotes || [];
    this.footnotesCache = footnotes || []; // Keep footnotesCache in sync
    this.resultsCache.clear();
    this.resultsCache.setItems(this.items); // Update resultsCache with items for heading stats

    // Populate resultsCache with cached_results from footnotes
    for (const fn of this.items) {
      if (fn.cached_results && Object.keys(fn.cached_results).length > 0) {
        const footnoteId = String(fn.id);
        this.resultsCache.storeResults(footnoteId, fn.cached_results);
      }
    }

    // Register citation types from any cached citations
    for (const fn of this.items) {
      if (fn.cached_citations && Array.isArray(fn.cached_citations)) {
        this.registerCitationTypes(fn.cached_citations);
      }
      if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
        this.registerCitationTypes(fn.parsed_citations);
      }
    }
  }

  setItems(items) {
    this.items = items || [];
    // Also update footnotesCache if it's not set or empty
    if (!this.footnotesCache || this.footnotesCache.length === 0) {
      this.footnotesCache = items || [];
    }
    this.resultsCache.setItems(this.items); // Update resultsCache with items for heading stats
  }

  isPdfParsePending(documentId) {
    return !!documentId && pdfParseInflightByDocument.has(documentId);
  }

  _applyPdfFootnotesResponse(responseData) {
    const { footnotes, pages, headings } = responseData || {};
    this.lastPdfParseMeta = {
      cached: Boolean(responseData?.cached),
      structuredCached: Boolean(responseData?.structured_cached),
    };

    this.resultsCache.clear();

    // Store in footnotesCache for consistency with Word add-in
    this.footnotesCache = (footnotes || []).map((f) => {
        // All the properties we need are now guaranteed from the backend
        const note = {
            id: f.id,                           // Numeric ID for backend compatibility
            reference_id: f.reference_id,       // Display ID (e.g., "p1-1")
            context: f.context || "",
            full_context: f.full_context || "",
            // Broader anchor-page window, including adjacent-page text. The
            // footnote parser needs this when a claim crosses a page break.
            page_context: f.page_context || "",
            claim: f.claim || null,
            text: f.text || "",
            page_numbers: f.page_numbers || [], // Preserved!
            heading_id: f.heading_id || null,   // Preserved!
            rich_text: f.rich_text || null,
            // Preserve cached data fields
            cached_citations: f.cached_citations,
            cached_results: f.cached_results,
            cached_resolution: f.cached_resolution,
            cached_congruence: f.cached_congruence,
            parsed_citations: f.parsed_citations,
            inline_citation: f.inline_citation,
        };
        
        return note;
    });

    // Keep this.items for backward compatibility with existing desktop app code
    this.items = [...this.footnotesCache];
    this.resultsCache.setItems(this.items); // Update resultsCache with items for heading stats

    // The API normally returns full Markdown in `markdown` and a definition-
    // free `main_text`. Older structured caches, however, may contain only
    // the latter or may contain definitions in a form the text renderer does
    // not recognize. Reattach only missing canonical definitions for display;
    // keep `main_text` unchanged because inline parsing relies on it being
    // footnote-free.
    const parsedFootnotesByPage = new Map();
    for (const footnote of this.footnotesCache) {
      for (const pageNumber of (footnote.page_numbers || [])) {
        const pageKey = String(pageNumber);
        if (!parsedFootnotesByPage.has(pageKey)) parsedFootnotesByPage.set(pageKey, []);
        parsedFootnotesByPage.get(pageKey).push(footnote);
      }
    }
    this.pages = (pages || []).map((page) => {
      const pageNumber = String(page.page_number ?? '');
      const pageFootnotes = parsedFootnotesByPage.get(pageNumber) || [];
      let markdown = page.markdown || page.main_text || '';
      const missingDefinitions = pageFootnotes
        .map((footnote) => {
          const match = String(footnote.reference_id || '').match(/p\d+-(\d+)$/);
          const number = match ? match[1] : String(footnote.id || '');
          if (!number || !footnote.text || new RegExp(`^\\[\\^${number.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\]:`, 'm').test(markdown)) {
            return null;
          }
          return `[^${number}]: ${footnote.text}`;
        })
        .filter(Boolean);
      if (missingDefinitions.length) {
        markdown = `${markdown.replace(/\s+$/, '')}\n\n${missingDefinitions.join('\n')}`;
      }
      return { ...page, markdown };
    });
    this.headings = headings || [];

    // Populate resultsCache with cached_results from footnotes
    // This enables heading stats to be calculated from cached data
    for (const footnote of this.footnotesCache) {
      if (footnote.cached_results && Object.keys(footnote.cached_results).length > 0) {
        const footnoteId = String(footnote.id);
        this.resultsCache.storeResults(footnoteId, footnote.cached_results);
      }
    }

    // Register citation types from cached data
    console.log('[FootnotesManager] loadFromPdfBytes: Registering citation types from', this.footnotesCache.length, 'footnotes');
    for (const fn of this.footnotesCache) {
      if (fn.cached_citations && Array.isArray(fn.cached_citations)) {
        this.registerCitationTypes(fn.cached_citations);
      }
      if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
        this.registerCitationTypes(fn.parsed_citations);
      }
    }
    
    return { items: this.footnotesCache, pages: this.pages, headings: this.headings };
  }

  async loadFromPdfBytes({ fileBytes, documentId, documentPath }) {
    console.log(`[FootnotesManager] loadFromPdfBytes called with:`);
    console.log(`[FootnotesManager]   documentId: ${documentId}`);
    console.log(`[FootnotesManager]   documentPath: ${documentPath || '(empty)'}`);

    this.state.fileBytes = fileBytes;
    this.state.documentId = documentId || `browser-${Date.now()}`;
    this.state.documentPath = documentPath || "";
    const parseDocumentId = this.state.documentId;

    // Reset citation type filters for new document
    this.resetCitationTypeFilters();

    if (!fileBytes || !fileBytes.length) {
      throw new Error("No PDF bytes provided. In Tauri, call pickFileWithTauri({ withBytes: true }).");
    }

    if (parseDocumentId && pdfParseInflightByDocument.has(parseDocumentId)) {
      return pdfParseInflightByDocument.get(parseDocumentId);
    }
    
    // Build a real File so the server sees the correct filename + mimetype
    const file = new File([fileBytes], "document.pdf", { type: "application/pdf" });
    
    const form = new FormData();
    form.append("file", file); // let the browser set the multipart boundary & part headers
    form.append("document_id", this.state.documentId);
    form.append("document_path", this.state.documentPath);
    
    const parsePromise = safeFetchJson(
      `${this.apiBase}/pdf/footnotes_md`,
      { method: "POST", body: form }
    ).then((responseData) => {
      return this._applyPdfFootnotesResponse(responseData);
    });

    if (parseDocumentId) {
      pdfParseInflightByDocument.set(parseDocumentId, parsePromise);
    }

    try {
      return await parsePromise;
    } finally {
      if (parseDocumentId) {
        pdfParseInflightByDocument.delete(parseDocumentId);
      }
    }
  }

  renderLeft() {
    renderFootnoteCards(this.footnoteListEl, this.items, this.headings, {
      onSelect: (item) => this.focusRight(item),
      onVerify: (footnoteId, card) => this.verifyFootnote(footnoteId, card),
      mgr: this,
    });
  }

  focusRight(item, { scroll = true } = {}) {
    // Use the numeric ID for consistency
    const fnId = item.id; // Use numeric ID instead of reference_id
    this._focusedFnId = String(fnId); // remembered so the highlight can be refreshed in place
    const page = (item.page_numbers && item.page_numbers[0])
      || Number((/^p(\d+)-/.exec(item.reference_id) || [])[1]) || 1;
    const inlineCitation = item.inline_citation || item.parsed_citations?.[0] || null;
    const inlineClaim = item.is_inline && inlineCitation?.claim?.trim()
      ? inlineCitation.claim
      : "";
    // Footnotes: highlight the LLM-extracted claim once parsed. Before a parse
    // (no claim yet) leave the context empty so the full passage is highlighted.
    const footnoteClaim = !item.is_inline
      ? (item.claim?.trim()
          || item.cached_citations?.find((c) => c?.claim?.trim())?.claim
          || item.parsed_citations?.find((c) => c?.claim?.trim())?.claim
          || "")
      : "";
    const fallbackContext = item.context || item.full_context || "";
    const claimTarget = item.is_inline ? inlineClaim : footnoteClaim;
    const focusContext = claimTarget || fallbackContext;
    const focusFullContext = claimTarget ? "" : (item.full_context || "");
    const focusTargetText = claimTarget || fallbackContext || item.text || "";
    const focusTargetKind = claimTarget ? "claim" : "context";
  
    const focus = {
      fnId,
      page,
      context: focusContext,
      fullContext: focusFullContext,
      text: item.text || "",
      targetText: focusTargetText,
      targetKind: focusTargetKind
    };

    renderPages(this.pages, focus, {
      pdfBytes: this.state.fileBytes
    });
    this._fetchPdfHighlightRects(item, focus).catch((err) => {
      console.warn('[PDF_HIGHLIGHTS] Could not fetch coordinate highlights:', err);
    });
  
    const pdfModeActive = document.querySelector('.doc-view-toggle-btn[data-view-mode="pdf"].active');
    const el = document.getElementById(`page-${page}`);
    if (el && scroll && !pdfModeActive) {
      el.classList.add("focused");
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => el.classList.remove("focused"), 1200);
    }

    const jump = document.getElementById("page-jump");
    if (jump) jump.value = String(page);

    // Use numeric ID for highlighting
    highlightFootnoteInList(this.footnoteListEl, fnId);
  }

  async _fetchPdfHighlightRects(item, focus) {
    const pdfModeActive = document.querySelector('.doc-view-toggle-btn[data-view-mode="pdf"].active');
    if (!pdfModeActive || !this.apiBase || !this.state.fileBytes?.length) return;

    const targetText = (focus.targetText || focus.context || focus.fullContext || focus.text || "").trim();
    if (targetText.length < 8) return;

    const cacheKey = [
      this.state.documentId || "document",
      focus.fnId || item?.id || "",
      focus.page || "",
      targetText.slice(0, 160),
    ].join("::");

    if (this._pdfHighlightCache.has(cacheKey)) {
      const cachedRects = this._pdfHighlightCache.get(cacheKey);
      renderPages(this.pages, { ...focus, highlightRects: cachedRects }, {
        pdfBytes: this.state.fileBytes
      });
      return;
    }

    const seq = ++this._pdfHighlightRequestSeq;
    const form = new FormData();
    form.append("file", new File([this.state.fileBytes], "document.pdf", { type: "application/pdf" }));
    form.append("targets", JSON.stringify([{
      id: String(focus.fnId || item?.id || ""),
      kind: focus.targetKind || (focus.context ? "claim" : "context"),
      page: focus.page || 1,
      text: targetText,
    }]));

    const data = await safeFetchJson(`${this.apiBase}/pdf/highlights`, {
      method: "POST",
      body: form,
    });

    const rects = data?.highlights?.[0]?.rects || [];
    this._pdfHighlightCache.set(cacheKey, rects);

    if (seq !== this._pdfHighlightRequestSeq || String(this._focusedFnId) !== String(focus.fnId)) {
      return;
    }

    renderPages(this.pages, { ...focus, highlightRects: rects }, {
      pdfBytes: this.state.fileBytes
    });
  }

  /**
   * Re-render the highlight for the currently focused footnote in place (no
   * scrolling). Called after a parse lands a claim so the highlight narrows
   * from the full context passage to the extracted claim automatically.
   */
  refreshFocusHighlight(fnId) {
    if (!this._focusedFnId || String(fnId) !== this._focusedFnId) return;
    const cache = Array.isArray(this.footnotesCache) && this.footnotesCache.length
      ? this.footnotesCache
      : (this.items || []);
    const item = cache.find((f) => String(f.id) === this._focusedFnId);
    if (item) this.focusRight(item, { scroll: false });
  }

  _syncParsedHighlightState(fnId, { claim, citations } = {}) {
    const lists = [this.footnotesCache, this.items].filter(Array.isArray);
    for (const list of lists) {
      const item = list.find((f) => String(f.id) === String(fnId));
      if (!item) continue;
      if (claim && claim.trim()) item.claim = claim;
      if (Array.isArray(citations)) item.parsed_citations = citations;
    }
    this.refreshFocusHighlight(fnId);
  }

    async refreshFootnotes(listEl, renderFn) {
    if (!this.currentDocumentId) throw new Error('No document ID available');

    const docx_zip_b64 = await this.platform.getDocxBase64();
    const document_path = await this.platform.getDocumentPath();
    const authHeaders = await this.getAuthHeaders();

    const response = await fetch(`${this.apiBase}/audit/all`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({ docx_zip_b64, document_id: this.currentDocumentId, document_path }),
    });

    if (!response.ok) throw new Error('Failed to fetch footnotes from server.');

    const data = await response.json();
    // Word renumbers footnote ids on save, so after an insert/delete an id can
    // now belong to a different note. Drop in-memory results for any id whose
    // text changed; the server re-keys its cache and returns the right ones.
    const previousText = new Map((this.footnotesCache || []).map(f => [String(f.id), f.text]));
    for (const f of (Array.isArray(data.footnotes) ? data.footnotes : [])) {
      const id = String(f.id);
      if (previousText.has(id) && previousText.get(id) !== f.text) this.resultsCache.deleteResults(id);
      previousText.delete(id);
    }
    for (const goneId of previousText.keys()) this.resultsCache.deleteResults(goneId);

    this.footnotesCache = Array.isArray(data.footnotes) ? data.footnotes : [];
    this.items = this.footnotesCache;
    this.headings = Array.isArray(data.headings) ? data.headings : [];
    this.resultsCache.setItems(this.items); // Update resultsCache with items for heading stats

    // Populate resultsCache with cached_results from backend
    // Store them without rendering since cards don't exist yet - they'll be auto-rendered when cards are created
    for (const footnote of this.footnotesCache) {
      if (footnote.cached_results && Object.keys(footnote.cached_results).length > 0) {
        const footnoteId = String(footnote.id);
        console.log(`[FOOTNOTES_MGR] Storing cached_results for footnote ${footnoteId}:`, Object.keys(footnote.cached_results).length, 'entries');
        // Store in resultsCache so auto-rendering can find them (don't render yet, cards don't exist)
        this.resultsCache.storeResults(footnoteId, footnote.cached_results);
      }
    }
    
    console.log('[FOOTNOTES_MGR] refreshFootnotes received:', {
      footnotesCount: this.footnotesCache.length,
      headingsCount: this.headings.length,
      headings: this.headings,
      sampleFootnotes: this.footnotesCache.slice(0, 3).map(f => ({ id: f.id, heading_id: f.heading_id }))
    });
    
    // Register citation types from cached citations
    for (const fn of this.footnotesCache) {
      if (fn.cached_citations && Array.isArray(fn.cached_citations)) {
        this.registerCitationTypes(fn.cached_citations);
      }
      if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
        this.registerCitationTypes(fn.parsed_citations);
      }
    }
    
    renderFn(listEl, this.footnotesCache, this.headings); // Pass headings to render function
    return 'Done.';
  }

  async verifyFootnote(id, cardEl = null, listEl = null, forceRecheck = false) {
    const key = String(id);
    if (this.inflight.has(key)) return;
    this.inflight.add(key);

    const targetCard = cardEl || this.footnoteListEl?.querySelector(`.card[data-id="${key}"]`);
    const resultsContainer = targetCard?.querySelector('.results');
    
    // Expand card and scroll to top so user can see the progress indicators
    if (targetCard) {
      const header = targetCard.querySelector('.card-header');
      const body = targetCard.querySelector('.card-body');
      if (header && body) {
        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        if (!isExpanded) {
          header.setAttribute('aria-expanded', 'true');
          body.classList.add('show');
        }
      }
      targetCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    // Check if this is a "Check Again" action
    const verifyBtn = targetCard?.querySelector('.card-body .row button');
    const isCheckAgain = forceRecheck || verifyBtn?.textContent?.trim() === 'Check Again';
    
    // Clear the rendered results before showing verification progress. Keep the
    // footnote's persisted cache: Check Again reuses a good resolution and
    // refreshes congruence only.
    if (this.resultsCache.hasResults(key)) {
      console.log(`[DEBUG] Clearing cached results for footnote ${key}${isCheckAgain ? ' (Check Again)' : ' (fresh verification)'}`);
      this.resultsCache.deleteResults(key);
      // Also clear the UI to show fresh state
      if (resultsContainer) {
        resultsContainer.innerHTML = '';
      }
    } else if (resultsContainer) {
      // Clear UI even if no cached results
      resultsContainer.innerHTML = '';
    }
    
    if (targetCard) setCardPending(targetCard, true, 'Parsing citations…');

    try {
      // Ensure footnotesCache is initialized
      if (!this.footnotesCache || !Array.isArray(this.footnotesCache)) {
        this.footnotesCache = this.items || [];
      }
      
      const footnote = this.footnotesCache.find(f => String(f.id) === key);
      if (!footnote) {
        throw new Error(`Footnote ${key} not found in cache.`);
      }

      const documentPath = await this.platform.getDocumentPath();
      
      // Get user's LLM settings (only non-default values are included)
      const userSettings = getSettingsForApiPayload();
      
      const commonPayload = {
        notes: this.footnotesCache,
        client_id: this.clientId,
        document_id: this.currentDocumentId,
        document_path: documentPath,
        internet_policy: this.isWebApp ? 'on' : (document.getElementById('internet-policy')?.value || 'smart'),
        data_source: window.footcheckDataSourceManager?.getSelectedDataSource?.() || 'internet',
        // Use settings from Settings tab, fall back to DOM element or defaults
        congruence_method: userSettings.congruence_method || document.getElementById('congruence-method')?.value || 'micro_tasks',
        // Include LLM model overrides (only non-default values)
        ...userSettings,
      };

      // Check if we have complete cached results (skip if Check Again)
      if (!isCheckAgain && footnote.cached_results && Object.keys(footnote.cached_results).length > 0) {
        console.log('[DEBUG] Using complete cached results for footnote', key);
        
        // Ensure file catalog is loaded
        if (this.fileCatalogManager.size === 0) {
          await this.fileCatalogManager.ensureFileCatalog().catch(err => {
            console.warn('[DEBUG] Failed to load file catalog for cached results:', err);
          });
        }
        
        // Render placeholders and results
        const citations = Object.values(footnote.cached_results).map(r => ({
          id: r.id || r.cite_id,
          source: r.source,
          context: r.context,
          file_id: r.file_id || r.file || r.resolve_result?.file_id
        }));
        renderInitialCitationPlaceholders(resultsContainer, citations);
        this.resultsCache.storeAndRenderResults(key, footnote.cached_results);
        if (targetCard) setCardPending(targetCard, false);
        return;
      }
      
      // Use the workflow to verify the footnote
      const workflowResult = await verifyFootnoteWorkflow(footnote, {
        apiBase: this.apiBase,
        getAuthHeaders: (options = {}) => this.getAuthHeaders(options),
        isWebApp: this.isWebApp,
        commonPayload,
        recheckCongruence: isCheckAgain,
        resultsContainer,
        onParsed: ({ claim, citations }) => {
          this._syncParsedHighlightState(key, { claim, citations });
        },
        onProgress: (stage, message) => {
          if (targetCard && stage !== 'complete') {
            setCardPending(targetCard, true, message);
          }
        }
      });
      
      // Extract results and register citation types
      const finalResults = workflowResult.results || workflowResult;
      if (workflowResult.parsedCitations) {
        this.registerCitationTypes(workflowResult.parsedCitations);
      }
      this.resultsCache.storeJointResults(key, workflowResult.jointResults || {});
      
      // NOTE: Individual verification (verifyFootnote) does NOT filter by type.
      // When a user explicitly clicks verify on a footnote, they want ALL citations verified,
      // regardless of filter settings. Type filtering only applies to verifyAllSmart.
      
      // Store and render final results (inspired by verifyAllSmart - only use resultsCache, don't update footnote.cached_results)
      this.resultsCache.storeAndRenderResults(key, finalResults);

    } catch (error) {
      console.error(`Verification failed for footnote ${key}:`, error);
      const errorMessage = extractErrorMessage(error);
      if (targetCard) setCardPending(targetCard, true, 'Verification failed');
      this.resultsCache.storeAndRenderResults(key, { "__error__": { label: "Error", rationale: errorMessage } });
    } finally {
      if (targetCard) setCardPending(targetCard, false);
      this.inflight.delete(key);
    }
  }

  /**
   * Seed resolution+congruence for a footnote that was just inserted from a
   * suggestion, so it renders as already-verified instead of pending.
   *
   * Assumes the caller has already re-synced footnotes (so the new footnote is in
   * `footnotesCache` and has a rendered card). Matches the footnote by text,
   * calls /suggest/seed_check, and renders the returned cached results.
   *
   * @param {Object} info - { footnoteText, fileId, sourceDb, title, congruence, snippets, selection }
   * @returns {Promise<boolean>} true if the footnote was seeded and rendered
   */
  async seedSuggestionInsertedFootnote(info = {}) {
    const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim();
    const target = norm(info.footnoteText);
    if (!target || !info.fileId) {
      console.warn('[SEED] Missing footnote text or file_id; skipping seed-on-insert');
      return false;
    }

    const cache = Array.isArray(this.footnotesCache) ? this.footnotesCache : (this.items || []);
    // Prefer a matching footnote that isn't already checked; fall back to any match.
    let fn = cache.find(f => norm(f.text) === target && !(f.cached_results && Object.keys(f.cached_results).length))
          || cache.find(f => norm(f.text) === target);
    if (!fn) {
      console.warn('[SEED] Inserted footnote not found by text after resync; skipping');
      return false;
    }

    const key = String(fn.id);
    try {
      const documentPath = await this.platform.getDocumentPath();
      const payload = {
        footnote_id: key,
        footnote_text: fn.text || info.footnoteText || '',
        file_id: info.fileId,
        source_db: info.sourceDb || null,
        snippets: info.snippets || [],
        congruence: info.congruence || null,
        selection: info.selection || '',
        title: info.title || null,
        document_id: this.currentDocumentId,
        document_path: documentPath,
        data_source: window.footcheckDataSourceManager?.getSelectedDataSource?.() || 'internet',
      };
      // Desktop/Word run against the local engine (bridge); web runs direct.
      if (!this.isWebApp) payload.client_id = this.clientId;

      const headers = { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) };
      const resp = await fetch(`${this.apiBase}/suggest/seed_check`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        cache: 'no-store',
      });
      const data = await resp.json().catch(() => null);
      if (resp.ok && data && data.status === 'ok' && data.cached_results && Object.keys(data.cached_results).length) {
        // Ensure file catalog is loaded so the source label renders.
        if (this.fileCatalogManager?.size === 0) {
          await this.fileCatalogManager.ensureFileCatalog().catch(() => {});
        }
        this.resultsCache.storeAndRenderResults(key, data.cached_results);
        return true;
      }
      console.warn('[SEED] seed_check returned no results:', data && (data.error || data.status));
      return false;
    } catch (e) {
      console.warn('[SEED] seed_check failed:', e);
      return false;
    }
  }

  /**
   * Helper function to check if a source type should be excluded.
   * @param {string|Object} sourceType - The source type (string, Precedent object, LegalNorm object, or other type objects)
   * @param {string[]} excludedTypes - Array of source type strings to exclude
   * @returns {boolean} - True if the source type should be excluded
   */
  /**
   * Get a citation by footnote ID and citation ID
   * Used by ask_user to display citation text
   */
  getCitationById(footnoteId, citationId) {
    // First check in-progress citations (during verifyAll)
    const inProgressCitations = this._inProgressCitations.get(String(footnoteId));
    if (inProgressCitations) {
      const citation = inProgressCitations.find(c => c.id === citationId);
      if (citation) {
        console.log(`[FootnotesManager] Found citation ${citationId} in _inProgressCitations`);
        return citation;
      }
    }
    
    // Fallback to cached citations
    const footnote = this.footnotesCache?.find(f => String(f.id) === String(footnoteId));
    if (footnote && footnote.cached_citations) {
      const citation = footnote.cached_citations.find(c => c.id === citationId);
      if (citation) {
        console.log(`[FootnotesManager] Found citation ${citationId} in footnotesCache`);
        return citation;
      }
    }
    
    // Also check parsed_citations for inline citations
    if (footnote && footnote.parsed_citations) {
      const citation = footnote.parsed_citations.find(c => c.id === citationId);
      if (citation) {
        console.log(`[FootnotesManager] Found citation ${citationId} in parsed_citations`);
        return citation;
      }
    }
    
    console.warn(`[FootnotesManager] Citation ${citationId} not found in footnote ${footnoteId}`);
    return null;
  }

  _shouldExcludeSourceType(sourceType, excludedTypes, options = {}) {
    const { ignoreTypeFilters = false } = options;
    // First check the user-controlled "Verify" filter.
    // Prefer DOM state (most robust across contexts), then fall back to the in-memory manager.
    let filterVerdict = null; // null = unknown, true = explicitly allowed, false = explicitly excluded
    if (!ignoreTypeFilters) {
      const typeKey = formatCitationType(sourceType);
      const domDecision = this._domShouldVerifyTypeKey(typeKey);
      if (domDecision === false) {
        console.log(`[VerifyAll] 🔇 Excluding via filter UI: ${typeKey}`);
        return true;
      }
      if (domDecision === true) {
        filterVerdict = true;
      } else if (domDecision === null && this.citationTypeFilter) {
        const mgrDecision = this.citationTypeFilter.shouldVerify(sourceType);
        if (!mgrDecision) {
          console.log(`[VerifyAll] 🔇 Excluding via filter manager: ${typeKey}`);
          return true;
        }
        filterVerdict = true;
      }
    }
    
    // Legacy exclusion logic for backward compatibility
    // Handle objects with subtype property (Academic, InCase, News, Other)
    // Check auto-exclusions FIRST, before checking excludedTypes
    if (sourceType && typeof sourceType === 'object' && 'subtype' in sourceType) {
      // Auto-exclusions are defaults only — allow users to override via filters.
      if (filterVerdict !== true) {
        if (sourceType.subtype === 'legal submission' || sourceType.subtype === 'factual exhibit') {
          console.log(`[VerifyAll] 🔇 Auto-excluding InCase type (default): ${sourceType.subtype}`);
          return true;
        }
        if (sourceType.subtype === 'other') {
          console.log(`[VerifyAll] 🔇 Auto-excluding Other > other type (default)`);
          return true;
        }
      }
      
      // Check against excluded types list for other subtypes
      if (excludedTypes && excludedTypes.length > 0) {
        return excludedTypes.includes(sourceType.subtype);
      }
    }
    
    // Handle Precedent object (has 'origin' property)
    if (sourceType && typeof sourceType === 'object' && 'origin' in sourceType) {
      // Precedents are not excluded by string type filters
      return false;
    }
    
    // Handle LegalNorm object (has 'jurisdiction' property)
    if (sourceType && typeof sourceType === 'object' && 'jurisdiction' in sourceType) {
      // Legal norms are not excluded by string type filters
      return false;
    }
    
    // Handle string types (legacy) - only if excludedTypes is provided
    if (excludedTypes && excludedTypes.length > 0 && typeof sourceType === 'string') {
      return excludedTypes.includes(sourceType);
    }
    
    return false;
  }

// Replace the entire method inside class FootnotesManager
async verifyAllSmart({ parse, resolve, analyze, excludedSourceTypes, footnoteIds, ignoreCitationTypeFilters, recheckIfComplete } = {}) {
  // NOTE: For web app mode (Cloud Run), we can use much higher parallelism since:
  // 1. Server handles connections, not browser (no 6-connection-per-host limit)
  // 2. Cloud Run scales horizontally to handle parallel requests
  // 3. HTTP/2 multiplexing removes connection pool bottlenecks
  // For Word add-in/desktop mode, we keep lower limits to prevent browser connection pool exhaustion.
  const defaultParallelism = this.isWebApp ? 8 : 4;
  parse = parse ?? defaultParallelism;
  resolve = resolve ?? defaultParallelism;
  analyze = analyze ?? defaultParallelism;
  
  console.log(`[VerifyAll] Using parallelism: parse=${parse}, resolve=${resolve}, analyze=${analyze} (isWebApp=${this.isWebApp})`);

  // Set default excluded source types based on context
  if (excludedSourceTypes === undefined) {
    // No default exclusions - InCase and Other>other are auto-excluded in _shouldExcludeSourceType
    excludedSourceTypes = [];
  }
  
  console.log(`[VerifyAll] Excluded source types: ${excludedSourceTypes.length > 0 ? excludedSourceTypes.join(', ') : 'none'}`);

  // Check if we're in batch mode (queue view) - suppress overlays
  const isBatchMode = !!window.__FC_BATCH_MODE__;
  const batchProgress = window.__FC_BATCH_PROGRESS__;
  const batchResults = window.__FC_BATCH_RESULTS__;
  const batchError = window.__FC_BATCH_ERROR__;

  // Initialize the verify-all overlay (only in non-batch mode)
  let overlay = null;
  if (!isBatchMode) {
    overlay = ensureVerifyAllOverlay();
    overlay.show();
    // Mark that we're routing asks to the overlay
    window.fcVerifyAllOverlay = overlay;
  }

  const batchQueue = createBatchVerificationQueue();
  const footnoteErrorStatus = new Map();
  const footnotesWithCitations = new Set();

  // Setup cancel handler
  let cancelled = false;
  if (overlay) {
    overlay.onCancel(() => {
      cancelled = true;
      console.log('[VerifyAll] Cancelled by user');
    });
  }
  
  // 0) Gate on data source selection - default to Internet if none selected
  const dsm = window.footcheckDataSourceManager;
  if (dsm) {
    // Auto-select Internet if no data source selected (for queue workflow)
    if (!dsm.hasDataSourceSelected?.()) {
      console.log('[VerifyAll] No data source selected, defaulting to Internet');
      dsm.setDefaultToInternetOnly?.();
    }

    const check = dsm.canProceedWithVerification?.();
    if (check && !check.canProceed) {
      overlay?.hide();
      batchError?.(`Verification blocked - ${check.reason}`);
      const activateTab = window.footcheckActivateTab;
      activateTab?.('source');
      dsm.showSourceSelectionMessage?.();
      const statusEl = document.getElementById('status');
      if (statusEl) statusEl.textContent = `⚠️ ${check.reason}. Please select a data source first.`;
      return "Verification blocked - no data source selected.";
    }
  }
  
  // Load file catalog so file names display properly
  try {
    await this.ensureFileCatalog();
    console.log('[VerifyAll] File catalog loaded:', this.fileCatalogManager.size, 'files');
  } catch (e) {
    console.warn('[VerifyAll] Failed to load file catalog:', e);
  }

  // 1) Inputs and shared payload
  const documentPath = await this.platform.getDocumentPath();
  
  // Get user's LLM settings (only non-default values are included)
  const userSettings = getSettingsForApiPayload();
  
  const commonPayload = {
    notes: this.footnotesCache,
    client_id: this.clientId,
    document_id: this.currentDocumentId,
    document_path: documentPath,
    internet_policy: document.getElementById('internet-policy')?.value,
    data_source: window.footcheckDataSourceManager?.getSelectedDataSource?.(),
    // Use settings from Settings tab, fall back to DOM element or defaults
    congruence_method: userSettings.congruence_method || document.getElementById('congruence-method')?.value || 'micro_tasks',
    // Include LLM model overrides (only non-default values)
    ...userSettings,
  };

  const getFinalResultLabel = (result) => result?.label ||
    result?.congruence_result?.label ||
    result?.analyze_result?.label ||
    result?.analyze_result?.congruence_result?.label;

  const getFinalAnalysisLabel = (result) => {
    if (!result || typeof result !== 'object') return null;
    return result?.congruence_result?.label ||
      result?.analyze_result?.label ||
      result?.analyze_result?.congruence_result?.label ||
      (
        result?.label &&
        (
          result?.rationale !== undefined ||
          result?.confidence !== undefined ||
          result?.calibration !== undefined
        )
          ? result.label
          : null
      );
  };

  const isFinalAnalysisResult = (result) => {
    if (!result || typeof result !== 'object') return false;
    if (result.status === 'error') return true;
    const label = String(getFinalAnalysisLabel(result) || '').toLowerCase();
    return !!label && !['pending', 'processing', 'parsing', 'resolving', 'analyzing', 'unknown'].includes(label);
  };

  const documentCheckFailed = (result) => {
    const resolution = result?.resolve_result || result;
    const verdict = String(resolution?.document_check_verdict || '').toLowerCase();
    if (!verdict) return false;
    return !['same_document', 'citation_discrepancy'].includes(verdict);
  };

  const hasCompleteCachedResults = (footnote) => {
    const cachedResults = footnote?.cached_results;
    if (!cachedResults || typeof cachedResults !== 'object') return false;

    const entries = Object.entries(cachedResults)
      .filter(([citationId]) => citationId !== '__error__');
    if (entries.length === 0) return !!cachedResults.__error__;

    return entries.every(([, result]) => {
      if (documentCheckFailed(result)) return false;
      return isFinalAnalysisResult(result);
    });
  };

  const getCachedResultForCitation = (footnoteId, citationId) => {
    const footnote = sourceList.find(fn => String(fn.id) === String(footnoteId));
    const fromFootnote = footnote?.cached_results?.[String(citationId)];
    if (fromFootnote) return fromFootnote;

    const fromResultsCache = this.resultsCache.getResults(String(footnoteId))?.[String(citationId)];
    return fromResultsCache || null;
  };

  const getCachedResolutionForCitation = (footnoteId, citation) => {
    const cached = getCachedResultForCitation(footnoteId, citation?.id);
    if (!cached) return null;
    if (documentCheckFailed(cached)) {
      console.log(`[VerifyAll] Cached resolution for ${citation?.id} has failed document_check; resolving again`);
      return null;
    }

    const resolution = cached.resolve_result || cached;
    if (!resolution || typeof resolution !== 'object') return null;
    if (isUnresolvedCrossRefTarget(resolution)) return null;

    // A group check-again should reuse a good source match, but must make a
    // fresh resolution attempt for a previously failed one.
    if (checkAgain && (resolution.status === 'error' || resolution.failure_reason)) {
      console.log(`[VerifyAll] Cached resolution for ${citation?.id} failed previously; resolving again`);
      return null;
    }

    const hasResolutionData =
      resolution.file_id !== undefined ||
      resolution.method !== undefined ||
      resolution.failure_reason !== undefined ||
      resolution.status === 'error';
    if (!hasResolutionData) return null;

    const normalized = {
      cite_id: resolution.cite_id ?? citation?.id,
      file_id: resolution.file_id ?? cached.file_id ?? null,
      method: resolution.method ?? cached.method,
      score: resolution.score ?? cached.score,
      failure_reason: resolution.failure_reason ?? cached.failure_reason,
      pinpoint: resolution.pinpoint,
      url: resolution.url ?? cached.url,
      paywalled: resolution.paywalled ?? cached.paywalled,
      web_exists: resolution.web_exists ?? cached.web_exists,
      failed_urls: resolution.failed_urls,
      agent_hits: resolution.agent_hits,
      agent_judgment_label: resolution.agent_judgment_label,
      agent_judgment_confidence: resolution.agent_judgment_confidence,
      agent_assessment: resolution.agent_assessment,
      document_check_verdict: resolution.document_check_verdict,
      document_check_confidence: resolution.document_check_confidence,
      document_check_rationale: resolution.document_check_rationale,
      frequent_doc_metadata: resolution.frequent_doc_metadata,
      source_db: resolution.source_db,
    };

    return Object.fromEntries(
      Object.entries(normalized).filter(([, value]) => value !== undefined)
    );
  };

  const hasFinalCachedAnalysisForCitation = (footnoteId, citationId) => {
    const cached = getCachedResultForCitation(footnoteId, citationId);
    if (!cached) return false;
    if (documentCheckFailed(cached)) return false;
    return isFinalAnalysisResult(cached);
  };

  // Source of truth for the run
  const baseSourceList = (Array.isArray(this.items) && this.items.length)
    ? this.items
    : (this.footnotesCache || []);

  let sourceList = baseSourceList;
  if (Array.isArray(footnoteIds) && footnoteIds.length > 0) {
    const idSet = new Set(footnoteIds.map(id => String(id)));
    sourceList = baseSourceList.filter(fn => idSet.has(String(fn.id)));
  }

  // A scoped Verify Group first fills any missing work.  Once every footnote in
  // that scope is complete, its next invocation becomes a check-again: retain
  // successful resolutions, retry failed ones, and re-run congruence for every
  // citation.  Verify All keeps its existing cache-first behaviour.
  const checkAgain = !!recheckIfComplete && sourceList.length > 0 &&
    sourceList.every(hasCompleteCachedResults);

  console.log(`[VerifyAll] ========== Starting verification ==========`);
  console.log(`[VerifyAll] Document ID: ${this.currentDocumentId}`);
  console.log(`[VerifyAll] Document path: ${documentPath}`);
  const scopeLabel = (Array.isArray(footnoteIds) && footnoteIds.length > 0)
    ? `subset (${sourceList.length}/${baseSourceList.length})`
    : `${sourceList.length} items`;
  console.log(`[VerifyAll] Source list: ${scopeLabel} (from ${this.items?.length ? 'items' : 'footnotesCache'})`);
  console.log(`[VerifyAll] Pages count: ${this.pages?.length || 0}`);
  console.log(`[VerifyAll] Batch mode: ${isBatchMode}`);
  console.log(`[VerifyAll] Data source: ${commonPayload.data_source}`);
  console.log(`[VerifyAll] Internet policy: ${commonPayload.internet_policy}`);

  // CRITICAL: Check if source list is empty (indicates PDF parsing didn't complete)
  if (sourceList.length === 0) {
    console.error(`[VerifyAll] ❌ SOURCE LIST IS EMPTY! PDF parsing may not have completed.`);
    console.error(`[VerifyAll] items: ${this.items?.length || 0}, footnotesCache: ${this.footnotesCache?.length || 0}`);
    overlay?.hide();
    batchError?.('No footnotes to verify - PDF parsing may not have completed');
    return "No footnotes to verify.";
  }

  for (const fn of sourceList) {
    const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
    batchQueue.registerRow(fn.id, {
      cardEl: card,
      onCancel: () => {
        footnoteErrorStatus.set(fn.id, true);
        card && setCardPending(card, false);
      },
      onRetry: async () => {
        await this.verifyFootnote(fn.id, card, this.footnoteListEl);
      }
    });
  }

  // Clear incomplete cached results so their missing citations can be filled.
  // In check-again mode, keep the results available for resolution reuse and
  // merge the freshly computed congruence results back into them.
  for (const fn of sourceList) {
    if (hasCompleteCachedResults(fn) || checkAgain) {
      continue;
    }
    const footnoteId = String(fn.id);
    if (this.resultsCache.hasResults(footnoteId)) {
      console.log(`[VerifyAll] Clearing cached results for footnote ${footnoteId}`);
      this.resultsCache.deleteResults(footnoteId);
      // Also clear the UI to show fresh state
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      if (card) {
        const resultsContainer = card.querySelector('.results');
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
        }
      }
    }
  }

  // 2) PARSE — parallelized
  const fnQueue = sourceList.slice();
  const allCitations = []; // { footnoteId, citation, claim, cardEl, resultsEl }
  const claimsByFootnote = new Map();
  
  // Initialize parse stage progress
  const totalFootnotes = sourceList.length;
  let parsedCount = 0;

  // Helper to update progress (overlay in normal mode, callback in batch mode)
  const updateProgress = (stage, current, total, status) => {
    overlay?.updateStage(stage, current, total, status);
    if (isBatchMode && batchProgress) {
      batchProgress(stage, current, total);
    }
  };

  updateProgress('parse', 0, totalFootnotes, 'active');
  updateProgress('resolve', 0, 0, 'pending');
  updateProgress('analyze', 0, 0, 'pending');

  // pre-mark UI as queued/parsing
  for (const fn of fnQueue) {
    const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
    const resultsEl = card?.querySelector('.results');
    card && setCardPending(card, true, 'Queued for parsing…');
    resultsEl && updateCitationUI(resultsEl, null, { state: 'parsing', message: 'Parsing citations…' });
    batchQueue.updateRowStatus(fn.id, 'queued');
  }

  const parseOne = async (fn) => {
    const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
    const resultsEl = card?.querySelector('.results');
    const startTime = Date.now();
    console.log(`[VerifyAll] 🚀 PARSE START: Footnote ${fn.id}`);
    
    try {
      if (batchQueue.isCancelled(fn.id)) {
        batchQueue.updateRowStatus(fn.id, 'error', 'Cancelled');
        card && setCardPending(card, false);
        return;
      }

      batchQueue.updateRowStatus(fn.id, 'parsing');
      card && setCardPending(card, true, 'Parsing citations…');

      if (hasCompleteCachedResults(fn) && !checkAgain) {
        console.log(`[VerifyAll] ✅ CACHE HIT: Footnote ${fn.id} has complete cached results`);
        if (fn.cached_citations && Array.isArray(fn.cached_citations)) {
          this.registerCitationTypes(fn.cached_citations);
        }
        if (fn.parsed_citations && Array.isArray(fn.parsed_citations)) {
          this.registerCitationTypes(fn.parsed_citations);
        }
        this.resultsCache.storeAndRenderResults(String(fn.id), fn.cached_results);
        card && setCardPending(card, false);
        batchQueue.updateRowStatus(fn.id, 'done', 'Cached');
        return;
      }

      let claim;
      let citations;
      
      // Use parseFootnote from workflow stages
      const parseResult = await parseFootnote(fn, {
        apiBase: this.apiBase,
        getAuthHeaders: (options = {}) => this.getAuthHeaders(options),
        isWebApp: this.isWebApp,
        commonPayload,
        skipCache: false,
        resultsContainer: resultsEl,
      });
      claim = parseResult.claim;
      citations = parseResult.citations;

      // Keep the footnote item in sync so claim highlighting works after parse,
      // and refresh the highlight in place if this footnote is currently focused
      this._syncParsedHighlightState(fn.id, { claim, citations });

      claimsByFootnote.set(fn.id, claim);

      // Register citation types for the filter panel
      this.registerCitationTypes(citations);

      // Render placeholders for this footnote
      resultsEl && renderInitialCitationPlaceholders(resultsEl, citations);

      // Store citations in _inProgressCitations for ask_user to access
      this._inProgressCitations.set(String(fn.id), citations);

      for (const c of citations) {
        // Each citation may carry its own LLM-extracted claim; fall back to the footnote-level one
        const citationClaim = c?.claim?.trim() ? c.claim : claim;
        allCitations.push({ footnoteId: fn.id, citation: c, claim: citationClaim, cardEl: card, resultsEl });
      }

      if (citations.length) {
        footnotesWithCitations.add(fn.id);
      } else {
        batchQueue.updateRowStatus(fn.id, 'done', 'No citations found');
      }

      card && setCardPending(card, true, citations.length ? 'Resolving sources…' : 'Parsing complete — no citations found');
      
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ✅ PARSE DONE: Footnote ${fn.id} (${elapsed}ms, ${citations.length} citations)`);
    } catch (e) {
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ❌ PARSE ERROR: Footnote ${fn.id} (${elapsed}ms) - ${extractErrorMessage(e)}`);
      // show error per footnote
      this.resultsCache.storeAndRenderResults(String(fn.id), { "__error__": { label: "Error", rationale: extractErrorMessage(e) } });
      card && setCardPending(card, false);
      footnoteErrorStatus.set(fn.id, true);
      batchQueue.updateRowStatus(fn.id, 'error', extractErrorMessage(e));
    } finally {
      // Update overlay progress
      parsedCount++;
      updateProgress('parse', parsedCount, totalFootnotes, 'active');
    }
  };

  // worker pool for parsing
  const numParseWorkers = Math.min(parse, fnQueue.length);
  console.log(`[VerifyAll] 🔄 Starting PARSE stage with ${numParseWorkers} workers for ${fnQueue.length} footnotes`);
  const parseStartTime = Date.now();
  
  const parseWorkers = Array.from({ length: numParseWorkers }, async () => {
    while (fnQueue.length && !cancelled) {
      const next = fnQueue.shift();
      if (!next) break;
      await parseOne(next);
    }
  });
  await Promise.all(parseWorkers);
  
  const parseElapsed = Date.now() - parseStartTime;
  console.log(`[VerifyAll] ⏱️  PARSE stage complete: ${parseElapsed}ms total, ${allCitations.length} citations found`);
  
  // Check for cancellation after parse stage
  if (cancelled) {
    console.log('[VerifyAll] Cancelled after parse stage');
    overlay?.hide();
    window.fcVerifyAllOverlay = null;
    batchError?.('Verification cancelled');
    // Clear pending states
    for (const fn of sourceList) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      card && setCardPending(card, false);
    }
    return "Verification cancelled.";
  }
  
  // Mark parse stage as complete
  updateProgress('parse', parsedCount, totalFootnotes, 'complete');

  // Filter citations by excluded source types (after parsing, before resolution)
  // Always run this check to apply auto-exclusions (InCase, Other>other)
  const excludedTypeKeys = new Set();
  const filterEntries = (entries, ignoreTypeFilters) => {
    excludedTypeKeys.clear();
    const included = [];
    const excluded = [];
    for (const entry of (entries || [])) {
      const sourceType = entry.citation?.source?.type;
      const shouldExclude = this._shouldExcludeSourceType(sourceType, excludedSourceTypes, {
        ignoreTypeFilters
      });
      if (shouldExclude) {
        const typeKey = formatCitationType(sourceType);
        if (typeKey && typeKey !== '—') {
          excludedTypeKeys.add(typeKey);
        }
        excluded.push(entry);
        console.log(`[VerifyAll] 🔇 Excluding citation ${entry.citation.id} with source type: ${typeof sourceType === 'string' ? sourceType : JSON.stringify(sourceType)}`);
      } else {
        included.push(entry);
      }
    }
    return { included, excluded };
  };

  const beforeFilterCount = allCitations.length;
  let resolveFilter = filterEntries(allCitations, !!ignoreCitationTypeFilters);
  let citationsForResolve = resolveFilter.included;
  let excludedAfterParse = resolveFilter.excluded;

  const filteredCount = beforeFilterCount - citationsForResolve.length;
  if (filteredCount > 0) {
    console.log(`[VerifyAll] 🔇 Filtered out ${filteredCount} citation(s) based on current filters`);
  }
  if (overlay?.setExcludedTypes) {
    overlay.setExcludedTypes(Array.from(excludedTypeKeys));
  }

  // Immediately update UI for excluded citations so they don't stay stuck on "Parsing..."
  if (excludedAfterParse.length > 0) {
    for (const entry of excludedAfterParse) {
      try {
        entry.resultsEl && updateCitationUI(entry.resultsEl, entry.citation?.id, { state: 'filtered', message: 'Excluded by filters' });
      } catch (e) {
        // ignore
      }
    }
    // If an entire footnote is excluded, clear its pending header stage
    const includedFootnotes = new Set(citationsForResolve.map(e => String(e.footnoteId)));
    const excludedFootnotes = new Set(excludedAfterParse.map(e => String(e.footnoteId)));
    for (const footnoteId of excludedFootnotes) {
      if (includedFootnotes.has(footnoteId)) continue;
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${CSS.escape(String(footnoteId))}"]`);
      card && setCardPending(card, false);
      batchQueue.updateRowStatus(footnoteId, 'done', 'Excluded by filters');
    }
  }

  // Now that we have the filtered set, mark only included footnotes as "resolving"
  {
    const includedFootnotes = new Set(citationsForResolve.map(e => String(e.footnoteId)));
    for (const footnoteId of includedFootnotes) {
      if (!batchQueue.isCancelled(footnoteId)) {
        batchQueue.updateRowStatus(footnoteId, 'resolving');
      }
    }
  }

  // Early exit: nothing to do
  if (citationsForResolve.length === 0) {
    for (const fn of sourceList) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      card && setCardPending(card, false);
      if (hasCompleteCachedResults(fn)) {
        batchQueue.updateRowStatus(fn.id, 'done', 'Cached');
        continue;
      }
      const hasError = footnoteErrorStatus.get(fn.id);
      batchQueue.updateRowStatus(fn.id, hasError ? 'error' : 'done', hasError ? 'See results' : 'No citations');
    }
    overlay?.showComplete();
    window.fcVerifyAllOverlay = null;
    batchResults?.(this.resultsCache.calculateSummary());
    return "Done (no citations found).";
  }

  // 3) GROUP & RESOLVE — resolve each unique work once, in parallel
  const buildGroups = (entries) => {
    const m = new Map(); // key -> [{footnoteId, citation, claim, resultsEl, cardEl}]
    for (const entry of (entries || [])) {
      const k = resolutionKeyFromCitation(entry.citation);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(entry);
    }
    return m;
  };

  let groups = buildGroups(citationsForResolve);
  let keys = Array.from(groups.keys());
  const resolvedByKey = new Map();

  for (const k of keys) {
    const bucket = groups.get(k) || [];
    const cachedEntry = bucket.find(entry => getCachedResolutionForCitation(entry.footnoteId, entry.citation));
    if (!cachedEntry) continue;

    const cachedResolution = getCachedResolutionForCitation(cachedEntry.footnoteId, cachedEntry.citation);
    resolvedByKey.set(k, cachedResolution);
    console.log(`[VerifyAll] ✅ RESOLVE CACHE HIT: key "${k.substring(0, 50)}..." from citation ${cachedEntry.citation.id}`);
  }
  // Cached singleton groups can be skipped completely. Deduplicated groups still
  // make a cheap resolve call so the bridge can persist the result under every
  // citation ID before the cross-reference phase.
  keys = keys.filter(k => !resolvedByKey.has(k) || (groups.get(k) || []).length > 1);
  
  console.log(`[VerifyAll] 📊 Grouped ${citationsForResolve.length} citations into ${groups.size} unique resolution keys`);
  if (resolvedByKey.size > 0) {
    console.log(`[VerifyAll] 📊 Reusing ${resolvedByKey.size} cached resolution groups; ${keys.length} groups need resolving`);
  }
  console.log(`[VerifyAll] 📊 Citation to key mapping:`);
  for (const entry of citationsForResolve) {
    const k = resolutionKeyFromCitation(entry.citation);
    console.log(`  - Citation ${entry.citation.id} (${entry.citation.source.raw.substring(0, 40)}...) → key: "${k.substring(0, 60)}..."`);
  }
  console.log(`[VerifyAll] 📊 First few keys:`, Array.from(keys).slice(0, 3).map(k => k.substring(0, 50)));
  
  // Initialize resolve stage progress
  let totalToResolve = keys.length;
  let resolvedCount = 0;
  updateProgress('resolve', 0, totalToResolve, 'active');

  const resolveOne = async (k, groupsMap) => {
    const bucket = groupsMap?.get(k) || [];
    // Resolve the group's RICHEST member: short forms share a key with the full
    // citation, and the fullest one gives resolution the most signal.
    const sample = pickRichestCitation(bucket, e => e.citation) || bucket[0];
    if (!sample) {
      console.warn('[VerifyAll] Resolve key has no citations:', k);
      return;
    }
    const startTime = Date.now();
    console.log(`[VerifyAll] 🚀 RESOLVE START: Citation key "${k.substring(0, 50)}..."`);
    
    try {
      const footnote = this.footnotesCache.find(f => f.id === sample.footnoteId);
      const parsedCitations = bucket.map(x => x.citation);
      
      // Use resolveCitation from workflow stages
      const resolveResult = await resolveCitation(
        sample.citation,
        footnote,
        parsedCitations,
        {
          apiBase: this.apiBase,
          getAuthHeaders: (options = {}) => this.getAuthHeaders(options),
          isWebApp: this.isWebApp,
          commonPayload,
          cachedResolution: null,
          skipCache: false,
          // In check-again mode, only groups without a reusable successful
          // resolution should bypass the hosted server cache. Successful
          // multi-citation groups may still need the alias-persistence call.
          forceResolutionRefresh: checkAgain && !resolvedByKey.has(k),
          resultsContainer: null, // Don't update UI here, do it later
          equivalentCitations: bucket.map(entry => entry.citation),
        }
      );
      
      // Get citation IDs for this group (used for logging)
      const citationIds = bucket.map(x => x.citation.id);
      console.log(`[VerifyAll]    → STORING in resolvedByKey with key: "${k.substring(0, 60)}..."`);
      console.log(`[VerifyAll]    → This result will be used by citations: [${citationIds.join(', ')}]`);
      resolvedByKey.set(k, resolveResult);
      
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ✅ RESOLVE DONE: "${k.substring(0, 50)}..." (${elapsed}ms), result:`, resolveResult ? 'present' : 'MISSING');
    } catch (e) {
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ❌ RESOLVE ERROR: "${k.substring(0, 50)}..." (${elapsed}ms) - ${extractErrorMessage(e)}`);
      resolvedByKey.set(k, { status: 'error', label: 'error', rationale: extractErrorMessage(e) });
    } finally {
      // Update overlay progress
      resolvedCount++;
      updateProgress('resolve', resolvedCount, totalToResolve, 'active');
    }
  };

  // Two-phase ordering: resolve "real" citations first, then cross-references.
  // Cross-refs (ibid/supra/"Smith, supra"…) resolve against an already-resolved
  // target, so running them after their targets turns them into cheap cache/index
  // lookups and avoids the race where a cross-ref fastpaths a not-yet-resolved
  // target (and any duplicate web search).
  const isXrefKey = (k) => !!(groups.get(k) || [])[0]?.citation?.is_cross_reference;
  const nonXrefKeys = keys.filter(k => !isXrefKey(k));
  const xrefKeys = keys.filter(k => isXrefKey(k));

  const runResolvePhase = async (phaseKeys, phaseLabel) => {
    if (!phaseKeys.length) return;
    const numWorkers = Math.min(resolve, phaseKeys.length);
    console.log(`[VerifyAll] 🔄 RESOLVE phase "${phaseLabel}" with ${numWorkers} workers for ${phaseKeys.length} unique citations`);
    const workers = Array.from({ length: numWorkers }, async (_, workerIdx) => {
      let processed = 0;
      while (phaseKeys.length && !cancelled) {
        const k = phaseKeys.shift();
        if (!k) break;
        await resolveOne(k, groups);
        processed++;
      }
      console.log(`[VerifyAll] 👷 Resolve worker ${workerIdx} (${phaseLabel}) finished, processed ${processed} items`);
    });
    await Promise.all(workers);
  };

  console.log(`[VerifyAll] 🔄 Starting RESOLVE stage for ${keys.length} unique citations (${nonXrefKeys.length} primary, ${xrefKeys.length} cross-ref)`);
  const resolveStartTime = Date.now();

  await runResolvePhase(nonXrefKeys, 'primary');
  if (!cancelled) await runResolvePhase(xrefKeys, 'cross-ref');

  // The split above only sees parser-flagged cross-refs. A bare short name
  // ("ICJ Statute, art. 62(1)") is promoted to a cross-ref by the resolver itself,
  // so it ran in the primary phase alongside its target and may have found it not
  // yet attempted. Every target has been attempted now — retry those once.
  const retryKeys = [...resolvedByKey.entries()]
    .filter(([k, res]) => groups.has(k) && isUnresolvedCrossRefTarget(res))
    .map(([k]) => k);
  if (!cancelled && retryKeys.length) {
    totalToResolve += retryKeys.length;
    await runResolvePhase(retryKeys, 'cross-ref retry');
  }

  const resolveElapsed = Date.now() - resolveStartTime;
  console.log(`[VerifyAll] ⏱️  RESOLVE stage complete: ${resolveElapsed}ms total`);
  
  // Check for cancellation after resolve stage
  if (cancelled) {
    console.log('[VerifyAll] Cancelled after resolve stage');
    overlay?.hide();
    window.fcVerifyAllOverlay = null;
    batchError?.('Verification cancelled');
    // Clear pending states
    for (const fn of sourceList) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      card && setCardPending(card, false);
    }
    return "Verification cancelled.";
  }
  
  // Re-evaluate the filter before analysis, so Verify All reflects any filter changes
  // that happened while parsing/resolving (Desktop App users often adjust filters mid-run).
  let analyzeFilter = filterEntries(allCitations, !!ignoreCitationTypeFilters);
  let citationsForAnalyze = analyzeFilter.included;
  if (overlay?.setExcludedTypes) {
    overlay.setExcludedTypes(Array.from(excludedTypeKeys));
  }

  // If filters expanded during resolve, resolve any newly-included keys now
  const groupsForAnalyze = buildGroups(citationsForAnalyze);
  const missingKeys = Array.from(groupsForAnalyze.keys()).filter(k => !resolvedByKey.has(k));
  if (missingKeys.length > 0) {
    console.log(`[VerifyAll] Filters changed during run; resolving ${missingKeys.length} additional key(s) before analysis`);
    totalToResolve += missingKeys.length;
    updateProgress('resolve', resolvedCount, totalToResolve, 'active');

    const extraKeys = missingKeys.slice();
    const numExtraResolveWorkers = Math.min(resolve, extraKeys.length);
    const extraResolveWorkers = Array.from({ length: numExtraResolveWorkers }, async () => {
      while (extraKeys.length && !cancelled) {
        const k = extraKeys.shift();
        if (!k) break;
        await resolveOne(k, groupsForAnalyze);
      }
    });
    await Promise.all(extraResolveWorkers);
  }

  // Mark resolve stage as complete
  updateProgress('resolve', resolvedCount, totalToResolve, 'complete');

  // Early exit: user excluded everything
  if (citationsForAnalyze.length === 0) {
    for (const fn of sourceList) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      card && setCardPending(card, false);
      if (hasCompleteCachedResults(fn)) {
        batchQueue.updateRowStatus(fn.id, 'done', 'Cached');
        continue;
      }
      const hasError = footnoteErrorStatus.get(fn.id);
      batchQueue.updateRowStatus(fn.id, hasError ? 'error' : 'done', hasError ? 'See results' : 'No citations');
    }
    overlay?.showComplete();
    window.fcVerifyAllOverlay = null;
    batchResults?.(this.resultsCache.calculateSummary());
    return "Done (no citations found).";
  }

  {
    const includedFootnotes = new Set(citationsForAnalyze.map(e => String(e.footnoteId)));
    for (const footnoteId of includedFootnotes) {
      if (!batchQueue.isCancelled(footnoteId)) {
        batchQueue.updateRowStatus(footnoteId, 'analyzing');
      }
    }

    // Any footnotes that had citations but are now fully excluded should not remain stuck on "Analyzing…"
    const allFootnotesWithAnyCitations = new Set(allCitations.map(e => String(e.footnoteId)));
    for (const footnoteId of allFootnotesWithAnyCitations) {
      if (includedFootnotes.has(footnoteId)) continue;
      if (batchQueue.isCancelled(footnoteId)) continue;
      batchQueue.updateRowStatus(footnoteId, 'done', 'Excluded by filters');
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${CSS.escape(String(footnoteId))}"]`);
      card && setCardPending(card, false);
    }
  }

  // Update UI for resolution state per citation (for citations that are in-scope)
  for (const { citation, resultsEl } of citationsForAnalyze) {
    const k = resolutionKeyFromCitation(citation);
    const rr = resolvedByKey.get(k);
    const state = rr?.status === 'error' ? 'error' : 'resolved';
    resultsEl && updateCitationUI(resultsEl, citation.id, {
      state,
      message: state === 'resolved' ? 'Resolved source' : 'Resolution failed'
    });
  }

  // Track which footnotes are being analyzed
  const footnotesBeingAnalyzed = new Set();
  for (const { footnoteId } of citationsForAnalyze) {
    footnotesBeingAnalyzed.add(footnoteId);
  }

  // 4) ANALYZE — per-citation (congruence analysis), but organized by footnote
  const footnotesToAnalyze = new Map(); // footnoteId -> [{ citation, resultsEl, cardEl }]
  
  // Group citations by footnote for better organization
  for (const { footnoteId, citation, resultsEl, cardEl } of citationsForAnalyze) {
    if (!footnotesToAnalyze.has(footnoteId)) {
      footnotesToAnalyze.set(footnoteId, []);
    }
    footnotesToAnalyze.get(footnoteId).push({ citation, resultsEl, cardEl });
  }

  const analyzeOne = async (item) => {
    const { footnoteId, citation, resultsEl, cardEl } = item;
    const key = resolutionKeyFromCitation(citation);
    const resolveResult = resolvedByKey.get(key);
    const startTime = Date.now();
    console.log(`[VerifyAll] 🚀 ANALYZE START: Footnote ${footnoteId}, Citation ${citation.id}`);
    console.log(`  - Citation raw: "${citation.source.raw.substring(0, 60)}..."`);
    console.log(`  - Resolution key: "${key.substring(0, 60)}..."`);
    console.log(`  - Resolve result:`, resolveResult ? { method: resolveResult.method, file_id: resolveResult.file_id, status: resolveResult.status } : 'MISSING');

    if (batchQueue.isCancelled(footnoteId)) {
      footnoteErrorStatus.set(footnoteId, true);
      batchQueue.updateRowStatus(footnoteId, 'error', 'Cancelled');
      cardEl && setCardPending(cardEl, false);
      return;
    }
    
    // Get existing results for this footnote to accumulate
    const existingResults = this.resultsCache.getResults(footnoteId) || {};
    const finalResults = { ...existingResults };

    const markAnalyzeDone = () => {
      const status = footnoteCompletionStatus.get(footnoteId);
      if (!status) return;
      status.completed++;
      analyzedCount++;
      updateProgress('analyze', analyzedCount, totalToAnalyze, 'active');
      if (status.completed >= status.total) {
        const card = this.footnoteListEl?.querySelector(`.card[data-id="${footnoteId}"]`);
        card && setCardPending(card, false);
        const hasError = footnoteErrorStatus.get(footnoteId);
        batchQueue.updateRowStatus(footnoteId, hasError ? 'error' : 'done', hasError ? 'See results' : null);
      }
    };

    // Set stage badge to "Checking congruence..." when analysis starts for this footnote
    if (footnoteCompletionStatus.get(footnoteId).completed === 0) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${footnoteId}"]`);
      card && setCardPending(card, true, 'Checking congruence…');
    }

    if (!checkAgain && hasFinalCachedAnalysisForCitation(footnoteId, citation.id)) {
      resultsEl && updateCitationUI(resultsEl, citation.id, { state: 'complete', message: 'Congruence check complete (cached)' });
      this.resultsCache.storeAndRenderResults(String(footnoteId), finalResults);
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ✅ ANALYZE CACHE HIT: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms)`);
      markAnalyzeDone();
      return;
    }

    // Re-check filters at analysis time (allows Verify All to adapt if user changes filters mid-run)
    if (this._shouldExcludeSourceType(citation?.source?.type, excludedSourceTypes, { ignoreTypeFilters: !!ignoreCitationTypeFilters })) {
      resultsEl && updateCitationUI(resultsEl, citation.id, { state: 'filtered', message: 'Excluded by filters' });
      // Keep existing rendered results; just mark this citation as completed for progress/accounting.
      this.resultsCache.storeAndRenderResults(String(footnoteId), finalResults);
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ⏭️  ANALYZE SKIP: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms) - excluded by filters`);
      markAnalyzeDone();
      return;
    }

    // Check if resolve result is missing
    if (!resolveResult) {
      finalResults[citation.id] = { 
        status: 'error', 
        label: 'error', 
        rationale: 'Resolution result missing - resolve stage may have failed for this citation', 
        cite: citation?.source?.raw 
      };
      resultsEl && updateCitationUI(resultsEl, citation.id, { state: 'error', message: 'Resolution result missing' });
      this.resultsCache.storeAndRenderResults(String(footnoteId), finalResults);
      footnoteErrorStatus.set(footnoteId, true);
      
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ⚠️  ANALYZE SKIP: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms) - resolve result missing`);
      markAnalyzeDone();
      return;
    }

    if (resolveResult?.status === 'error') {
      finalResults[citation.id] = { status: 'error', label: 'error', rationale: resolveResult.rationale, cite: citation?.source?.raw };
      resultsEl && updateCitationUI(resultsEl, citation.id, { state: 'error', message: 'Resolution failed' });
      this.resultsCache.storeAndRenderResults(String(footnoteId), finalResults);
      footnoteErrorStatus.set(footnoteId, true);
      
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ⚠️  ANALYZE SKIP: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms) - resolution error`);
      markAnalyzeDone();
      return;
    }

    try {
      // Use analyzeCitation from workflow stages
      const footnote = this.footnotesCache.find(f => f.id === footnoteId);
      const analyzeResult = await analyzeCitation(
        citation,
        resolveResult,
        citation?.claim?.trim() ? citation.claim : claimsByFootnote.get(footnoteId),
        footnote?.quote,
        {
          apiBase: this.apiBase,
          getAuthHeaders: (options = {}) => this.getAuthHeaders(options),
          isWebApp: this.isWebApp,
          commonPayload,
          footnote,
          parsedCitations: this._getParsedCitationsForFootnote(footnote),
          cachedCongruence: null,
          skipCache: false,
          resultsContainer: resultsEl,
        }
      );

      finalResults[citation.id] = analyzeResult;
      
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ✅ ANALYZE DONE: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms)`);
    } catch (e) {
      const elapsed = Date.now() - startTime;
      console.log(`[VerifyAll] ❌ ANALYZE ERROR: Footnote ${footnoteId}, Citation ${citation.id} (${elapsed}ms) - ${extractErrorMessage(e)}`);
      finalResults[citation.id] = { status: 'error', label: 'error', rationale: extractErrorMessage(e), cite: citation?.source?.raw };
      resultsEl && updateCitationUI(resultsEl, citation.id, { state: 'error', message: 'Congruence failed' });
      footnoteErrorStatus.set(footnoteId, true);
    } finally {
      this.resultsCache.storeAndRenderResults(String(footnoteId), finalResults);
      
      markAnalyzeDone();
    }
  };

  // Process citations - footnotes in parallel, but citations within each footnote sequentially
  // This prevents race conditions when multiple citations try to update the same footnote's results
  const footnoteQueue = Array.from(footnotesToAnalyze.keys());

  // Track completion status per footnote
  const footnoteCompletionStatus = new Map();
  for (const footnoteId of footnotesBeingAnalyzed) {
    const citationCount = footnotesToAnalyze.get(footnoteId)?.length || 0;
    footnoteCompletionStatus.set(footnoteId, { total: citationCount, completed: 0 });
  }
  
  // Initialize analyze stage progress
  const totalToAnalyze = citationsForAnalyze.length;
  let analyzedCount = 0;
  updateProgress('analyze', 0, totalToAnalyze, 'active');

  // Use analyze parameter for parallelism
  const numAnalyzeWorkers = Math.min(analyze, footnoteQueue.length);
  console.log(`[VerifyAll] 🔄 Starting ANALYZE stage with ${numAnalyzeWorkers} workers for ${footnoteQueue.length} footnotes (${totalToAnalyze} total citations)`);
  const analyzeStartTime = Date.now();
  
  // Each worker processes complete footnotes (all citations sequentially)
  const analyzeWorkers = Array.from({ length: numAnalyzeWorkers }, async (_, workerIdx) => {
    console.log(`[VerifyAll] 👷 Analyze worker ${workerIdx} started`);
    let processed = 0;
    while (footnoteQueue.length && !cancelled) {
      const footnoteId = footnoteQueue.shift();
      if (!footnoteId) break;
      
      const citations = footnotesToAnalyze.get(footnoteId) || [];
      console.log(`[VerifyAll] 👷 Analyze worker ${workerIdx} processing footnote ${footnoteId} with ${citations.length} citations`);
      
      // Process all citations in this footnote sequentially
      for (const { citation, resultsEl, cardEl } of citations) {
        if (cancelled) break;
        await analyzeOne({ footnoteId, citation, resultsEl, cardEl });
      }
      if (!cancelled) {
        const footnote = this._findFootnote(footnoteId);
        const parsed = this._getParsedCitationsForFootnote(footnote);
        const groups = new Map();
        for (const citation of parsed) {
          if (!citation.requires_joint_evaluation || !citation.joint_evaluation_group) continue;
          if (!groups.has(citation.joint_evaluation_group)) groups.set(citation.joint_evaluation_group, []);
          groups.get(citation.joint_evaluation_group).push(citation);
        }
        const jointResults = {};
        for (const [groupId, members] of groups) {
          const results = this.resultsCache.getResults(footnoteId) || {};
          const memberResults = members.map(citation => results[citation.id]?.joint_member_result || {
            cite_id: citation.id, contribution_label: 'non-verifiable', calibration: 0,
            rationale: results[citation.id]?.rationale || 'Member check did not complete.',
            evidence: [], supported_claim_indices: [], quote_matches: [], unmatched_quotes: [],
          });
          try {
            jointResults[groupId] = await analyzeJointGroup(
              groupId, members.map(c => ({ ...c, resolve_result: results[c.id]?.resolve_result || results[c.id] })),
              memberResults,
              members[0]?.claim?.trim() || claimsByFootnote.get(footnoteId), footnote,
              { apiBase: this.apiBase, getAuthHeaders: (options = {}) => this.getAuthHeaders(options),
                isWebApp: this.isWebApp, commonPayload },
            );
          } catch (error) {
            jointResults[groupId] = { group_id: groupId, label: 'error',
              rationale: extractErrorMessage(error), member_cite_ids: members.map(c => c.id) };
          }
        }
        this.resultsCache.storeJointResults(footnoteId, jointResults);
        if (groups.size) this.resultsCache.storeAndRenderResults(
          String(footnoteId), this.resultsCache.getResults(footnoteId) || {});
      }
      processed++;
    }
    console.log(`[VerifyAll] 👷 Analyze worker ${workerIdx} finished, processed ${processed} footnotes`);
  });
  await Promise.all(analyzeWorkers);
  
  const analyzeElapsed = Date.now() - analyzeStartTime;
  console.log(`[VerifyAll] ⏱️  ANALYZE stage complete: ${analyzeElapsed}ms total`);
  
  // Check for cancellation after analyze stage
  if (cancelled) {
    console.log('[VerifyAll] Cancelled after analyze stage');
    overlay?.hide();
    window.fcVerifyAllOverlay = null;
    batchError?.('Verification cancelled');
    // Clear pending states
    for (const fn of sourceList) {
      const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
      card && setCardPending(card, false);
    }
    return "Verification cancelled.";
  }
  
  // Mark analyze stage as complete
  updateProgress('analyze', analyzedCount, totalToAnalyze, 'complete');

  // 5) tidy UI - show completion status
  for (const fn of sourceList) {
    const card = this.footnoteListEl?.querySelector(`.card[data-id="${fn.id}"]`);
    if (card) {
      // Check if this footnote had citations and results
      const hasResults = this.resultsCache.hasResults(String(fn.id));
      if (hasResults) {
        // Clear pending state to show final results
        setCardPending(card, false);
      } else {
        // No citations found
        setCardPending(card, false);
      }
    }
  }

  // Calculate results summary for batch mode
  if (isBatchMode && batchResults) {
    // Use resultsCache.calculateSummary() for consistent categorization
    const resultsSummary = this.resultsCache.calculateSummary();
    console.log(`[VerifyAll] ========== Verification complete ==========`);
    console.log(`[VerifyAll] Document ID: ${this.currentDocumentId}`);
    console.log(`[VerifyAll] Results cache size: ${this.resultsCache.size}`);
    console.log('[VerifyAll] Results summary:', resultsSummary);
    batchResults(resultsSummary);
  }

  // Show completion state
  overlay?.showComplete();

  // Clean up
  window.fcVerifyAllOverlay = null;
  return "Done.";
}



handleAskUser(msg, clientId, appEl) {
  const { question_id, question, footnote_id, cite_id } = msg;
  console.log('[ASK_USER] ========== handleAskUser called ==========');
  console.log('[ASK_USER] Full message:', msg);
  console.log('[ASK_USER] question_id:', question_id);
  console.log('[ASK_USER] footnote_id:', footnote_id, '(type:', typeof footnote_id, ')');
  console.log('[ASK_USER] cite_id:', cite_id);
  console.log('[ASK_USER] question preview:', question?.substring(0, 100));
  
  if (!question_id || !question) {
    console.error('[ASK_USER] Missing question_id or question, aborting');
    return;
  }

  // If we have an active verify-all overlay, inject the ask into it
  const overlay = window.fcVerifyAllOverlay;
  if (overlay && overlay.isVisible()) {
    try {
      console.log('[ASK_USER] Verify-all overlay is active, injecting into overlay');
      // Add the resolution item to the overlay
      overlay.addResolution(
        footnote_id,
        cite_id,
        question,
        question_id,
        clientId,
        this.currentDocumentId,
        this.apiBase,
        this.databaseManager,
        this  // Pass footnotes manager so overlay can look up citation
      );
      return;
    } catch (e) {
      console.error('[VerifyAllOverlay] Failed to inject ask, falling back to modal:', e);
      // fall-through to normal handling
    }
  }

  // Default behavior (inline on the footnote card or modal)
  if (footnote_id) {
    try {
      console.log(`[ASK_USER] Looking for card with data-id="${footnote_id}"`);
      const card = appEl.querySelector(`.card[data-id="${CSS.escape(String(footnote_id))}"]`);
      console.log(`[ASK_USER] Found card:`, !!card);
      
      if (!card) {
        console.error(`[ASK_USER] Card not found for footnote_id="${footnote_id}", available cards:`, 
          Array.from(appEl.querySelectorAll('.card')).map(c => c.dataset.id));
        throw new Error(`Footnote card not found for id=${footnote_id}`);
      }
      
      if (card && card.querySelector('.card-header').getAttribute('aria-expanded') !== 'true') {
        console.log('[ASK_USER] Opening card');
        card.querySelector('.card-header').click();
      }
      console.log('[ASK_USER] Calling showInlineAsk');
      showInlineAsk(appEl, footnote_id, cite_id, question, question_id, clientId, this.currentDocumentId, this.apiBase, this.databaseManager, this);
      console.log('[ASK_USER] showInlineAsk completed successfully');
    } catch (e) {
      console.error("[ASK_USER] Inline ask failed, falling back to modal:", e);
      this._fallbackAskModal(question_id, question);
    }
  } else {
    console.log('[ASK_USER] No footnote_id, using modal');
    this._fallbackAskModal(question_id, question);
  }
}

async _fallbackAskModal(question_id, question) {
  try {
    const answer = await showResolveDialog(question);
    await safeFetchJson(`${this.apiBase}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_id, answer })
    });
  } catch (e) {
    console.error('Modal ask failed:', e);
  }
}

  /**
   * Clear all state when a new file is selected
   */
  clearState() {
    this.items = [];
    this.headings = [];
    this.pages = [];
    this.footnotesCache = [];
    this.fileCatalogManager.clear();
    this.resultsCache.clear();
    
    // Clear any active pollers
    this.pollers.forEach((poller, id) => {
      clearInterval(poller);
    });
    this.pollers.clear();
    
    // Clear any inflight operations
    this.inflight.clear();
    
    console.log('[DEBUG] FootnotesManager state cleared');
  }

  setFileCatalog(files = [], sourceBasePath = '') {
    this.fileCatalogManager.setFileCatalog(files, sourceBasePath);
  }

  async ensureFileCatalog() {
    const files = await this.fileCatalogManager.ensureFileCatalog();
    if (Array.isArray(files) && files.length > 0) {
      return files;
    }

    // Fallback: Data Source tab hydration may not have run yet.
    // Pull directly from DatabaseManager cache/backend so "Choose file"
    // works immediately after loading a document.
    if (this.databaseManager?.loadFileList) {
      try {
        let dbFiles = await this.databaseManager.loadFileList();
        if (!Array.isArray(dbFiles) || dbFiles.length === 0) {
          // Avoid serving a stale cached empty list.
          dbFiles = await this.databaseManager.loadFileList(true);
        }
        if (Array.isArray(dbFiles) && dbFiles.length > 0) {
          const sourceBasePath = this.databaseManager?.getSourceFolder?.() || '';
          this.setFileCatalog(dbFiles, sourceBasePath);
          return Array.from(this.fileCatalog.values());
        }
      } catch (err) {
        console.warn('[FootnotesManager] DatabaseManager file list fallback failed:', err);
      }
    }

    return files;
  }

  getFileDisplay(entry) {
    return this.fileCatalogManager.getFileDisplay(entry);
  }

  getFileSourceUrl(fileId, sourceDb = 'doc') {
    return this.fileCatalogManager.getFileSourceUrl(fileId, sourceDb);
  }

  get fileCatalog() {
    return this.fileCatalogManager?.catalog || new Map();
  }

  async reassignCitationFile(footnoteId, citeId) {
    try {
      const files = await this.ensureFileCatalog();
      if (!files.length) {
        this.platform?.setStatus?.('No indexed files available to assign.');
        return;
      }

      const currentResults = this.resultsCache.getResults(footnoteId) || {};
      const currentEntry = currentResults?.[citeId];
      const currentDisplay = this.getFileDisplay(currentEntry || {});

      const selection = await showFilePickerDialog(files, currentDisplay.fileId);
      if (!selection) return;

      const selected = files.find(file => String(file.file_id) === String(selection));
      if (!selected) return;

      if (String(currentDisplay.fileId || '') === String(selection)) {
        this.platform?.setStatus?.('Citation already linked to this file.');
        return;
      }

      if (!this.currentDocumentId) {
        throw new Error('No active document.');
      }

      const documentPath = this.platform?.getDocumentPath ? await this.platform.getDocumentPath() : null;
      const dataSourceManager = window.footcheckDataSourceManager;
      const dataSource = dataSourceManager?.getSelectedDataSource?.() || 'internet';

      const authHeaders = await this.getAuthHeaders();
      const res = await fetch(`${this.apiBase}/audit/manual_resolution`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          document_id: this.currentDocumentId,
          document_path: documentPath,
          client_id: this.clientId,
          data_source: dataSource,
          citation_id: citeId,
          file_id: Number(selected.file_id),
          method: 'manual-override',
          score: 1.0,
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.status === 'error') {
        const errMsg = payload?.error || `Failed to update citation (HTTP ${res.status})`;
        throw new Error(errMsg);
      }

      const resultPayload = payload?.result || {};
      const updatedResults = this.resultsCache.cloneResults(currentResults);
      const entry = { ...(updatedResults?.[citeId] || {}) };
      entry.file = Number(selected.file_id);
      entry.file_id = Number(selected.file_id);
      entry.file_path = resultPayload.file_path || selected.path;
      entry.file_title = resultPayload.file_title || selected.title;
      entry.method = resultPayload.method || 'manual-override';
      entry.score = resultPayload.score ?? 1.0;
      entry.resolve_result = {
        ...(entry.resolve_result || {}),
        ...resultPayload,
      };

      updatedResults[citeId] = entry;
      const footnote = this._findFootnote(footnoteId);
      if (footnote?.cached_results) {
        footnote.cached_results[citeId] = { ...entry, resolve_result: entry.resolve_result };
      }
      if (entry.joint_evaluation_group) {
        this.resultsCache.invalidateJointGroup(footnoteId, entry.joint_evaluation_group);
        const groups = { ...this.resultsCache.getJointResults(footnoteId) };
        delete groups[entry.joint_evaluation_group];
        this.resultsCache.storeJointResults(footnoteId, groups);
        for (const memberEntry of Object.values(updatedResults)) {
          if (memberEntry?.joint_evaluation_group === entry.joint_evaluation_group) {
            delete memberEntry.joint_group_result;
          }
        }
      }
      this.setFileCatalog(files, this.fileCatalogManager?.sourceBasePath || ''); // refresh cache with any metadata changes
      this.resultsCache.storeAndRenderResults(footnoteId, updatedResults);
      this.platform?.setStatus?.('Citation source updated.');
      if (entry.joint_evaluation_group) await this.verifyFootnote(footnoteId, null, null, true);
    } catch (err) {
      console.error('Failed to reassign citation file:', err);
      this.platform?.setStatus?.(`Unable to update citation: ${err.message || err}`);
    }
  }

  _findFootnote(footnoteId) {
    const key = String(footnoteId);
    return this.footnotesCache?.find(f => String(f.id) === key)
      || this.items?.find(f => String(f.id) === key)
      || null;
  }

  _getParsedCitationsForFootnote(footnote) {
    if (!footnote) return [];
    if (Array.isArray(footnote.parsed_citations) && footnote.parsed_citations.length) return footnote.parsed_citations;
    if (Array.isArray(footnote.cached_citations) && footnote.cached_citations.length) return footnote.cached_citations;
    if (footnote.inline_citation) return [footnote.inline_citation];
    return [];
  }

  async resetCitationResult(footnoteId, citeId, interaction = {}) {
    const footnote = this._findFootnote(footnoteId);
    const parsedCitations = this._getParsedCitationsForFootnote(footnote);
    const citation = parsedCitations.find(c => String(c.id) === String(citeId)) || null;
    const resultEntry = this.resultsCache.getResults(footnoteId)?.[String(citeId)] || null;
    const resolveEntry = resultEntry?.resolve_result || resultEntry || {};
    const fallbackFileId = resolveEntry.file ?? resultEntry?.file;
    const fileId = resolveEntry.file_id
      ?? resultEntry?.file_id
      ?? (/^\d+$/.test(String(fallbackFileId ?? '')) ? Number(fallbackFileId) : null);
    const sourceDb = resolveEntry.source_db ?? resultEntry?.source_db ?? null;
    const catalogInfo = fileId != null && (!sourceDb || sourceDb === 'doc')
      ? this.fileCatalogManager?.catalog?.get?.(String(fileId))
      : null;
    const sourceUrl = resolveEntry.url
      ?? resultEntry?.source_url
      ?? catalogInfo?.source_url
      ?? null;
    const resolutionMethod = String(resolveEntry.method ?? resultEntry?.method ?? '').toLowerCase();
    const isFetchedSource = (!sourceDb || sourceDb === 'doc') && Boolean(sourceUrl) && (
      resolutionMethod.includes('web')
      || /(?:^|[\\/])resolved_sources(?:[\\/]|$)/i.test(String(catalogInfo?.path ?? ''))
    );
    const secondAction = isFetchedSource
      ? 'delete the fetched source'
      : 'exclude this source for the cited work';
    const resetDocumentId = this.currentDocumentId;
    const armKey = `${String(resetDocumentId)}:${String(footnoteId)}:${String(citeId)}`;
    const deleteSource = interaction?.button?.dataset?.sourceDeleteArmed === 'true';
    if (deleteSource) {
      const armed = this._citationResetArms.get(armKey);
      if (armed) window.clearTimeout(armed.timer);
    }

    if (!this.currentDocumentId) {
      this.platform?.setStatus?.('No active document.');
      return;
    }

    try {
      if (interaction?.button) interaction.button.disabled = true;
      this.platform?.setStatus?.(deleteSource ? 'Removing citation source...' : 'Clearing citation result...');

      const documentPath = this.platform?.getDocumentPath ? await this.platform.getDocumentPath() : null;
      const dataSource = window.footcheckDataSourceManager?.getSelectedDataSource?.() || 'internet';
      const endpoint = this.isWebApp ? '/web/audit/reset_cached_citation' : '/audit/reset_cached_citation';
      const requestBody = JSON.stringify({
        document_id: this.currentDocumentId,
        document_path: documentPath,
        client_id: this.isWebApp ? undefined : this.clientId,
        data_source: dataSource,
        citation_id: String(citeId),
        citation,
        file_id: fileId,
        source_db: sourceDb,
        source_url: sourceUrl,
        delete_source: deleteSource,
      });

      const sendResetRequest = async (authOptions = {}) => {
        const authHeaders = await this.getAuthHeaders(authOptions);
        return fetch(`${this.apiBase}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: requestBody,
        });
      };

      let res = await sendResetRequest();
      if (res.status === 401) {
        res = await sendResetRequest({ forceRefresh: true });
      }

      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.status === 'error') {
        throw new Error(payload?.error || payload?.detail || `Reset failed (HTTP ${res.status})`);
      }

      if (footnote?.cached_results) {
        delete footnote.cached_results[String(citeId)];
        const groupId = citation?.joint_evaluation_group;
        if (groupId) {
          for (const member of Object.values(footnote.cached_results)) {
            if (member?.joint_evaluation_group === groupId) delete member.joint_group_result;
          }
        }
      }
      if (citation?.joint_evaluation_group) {
        this.resultsCache.invalidateJointGroup(footnoteId, citation.joint_evaluation_group);
      }
      if (footnote?.cached_resolution && String(parsedCitations[0]?.id) === String(citeId)) {
        delete footnote.cached_resolution;
      }
      if (footnote?.cached_congruence && String(parsedCitations[0]?.id) === String(citeId)) {
        delete footnote.cached_congruence;
      }

      const hasSource = fileId != null || Boolean(sourceUrl);
      if (!deleteSource && hasSource) {
        this._armCitationSourceDeletion(
          armKey,
          interaction?.button,
          () => {
            const currentEntry = this.resultsCache.getResults(footnoteId)?.[String(citeId)] || null;
            if (this.currentDocumentId === resetDocumentId && currentEntry === resultEntry) {
              this.resultsCache.deleteCitationResult(footnoteId, citeId, parsedCitations);
              this.platform?.setStatus?.('Citation result cleared.');
            }
          },
          secondAction,
        );
        this.platform?.setStatus?.(
          `Resolution cleared. Click the changed icon again to ${secondAction}.`
        );
        return payload;
      }

      this._clearCitationSourceDeletionArm(armKey);
      this.resultsCache.deleteCitationResult(footnoteId, citeId, parsedCitations);
      if (payload?.removed_file && fileId != null) {
        this.fileCatalogManager?.catalog?.delete?.(String(fileId));
      }
      this.platform?.setStatus?.(
        deleteSource
          ? (payload?.removed_file
              ? 'Fetched source removed; citation ready to recheck.'
              : 'Source excluded for this cited work; citation ready to recheck.')
          : 'Citation result cleared.'
      );
      return payload;
    } catch (err) {
      console.error('Failed to reset citation result:', err);
      this.platform?.setStatus?.(`Unable to reset citation: ${err.message || err}`);
    } finally {
      if (interaction?.button?.isConnected) interaction.button.disabled = false;
    }
  }

  _armCitationSourceDeletion(armKey, button, finalize, actionLabel) {
    this._clearCitationSourceDeletionArm(armKey);
    if (!button) {
      finalize();
      return;
    }

    button.dataset.sourceDeleteArmed = 'true';
    button.classList.add('is-source-delete-armed');
    button.setAttribute('aria-label', 'Remove citation source');
    button.title = `Click again to ${actionLabel}`;
    button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6 3h8l4 4v7"></path>
        <path d="M14 3v5h5"></path>
        <path d="M6 3v18h7"></path>
        <path d="M16 16l5 5"></path>
        <path d="M21 16l-5 5"></path>
      </svg>
    `;

    const hint = document.createElement('span');
    hint.className = 'citation-source-delete-hint';
    hint.setAttribute('role', 'status');
    hint.textContent = `Click again to ${actionLabel}`;
    button.insertAdjacentElement('afterend', hint);

    const timer = window.setTimeout(() => {
      this._citationResetArms.delete(armKey);
      hint.remove();
      finalize();
    }, SOURCE_DELETE_ARM_MS);
    this._citationResetArms.set(armKey, { timer, button, hint });
  }

  _clearCitationSourceDeletionArm(armKey) {
    const armed = this._citationResetArms.get(armKey);
    if (!armed) return;
    window.clearTimeout(armed.timer);
    armed.hint?.remove();
    if (armed.button?.isConnected) {
      delete armed.button.dataset.sourceDeleteArmed;
      armed.button.classList.remove('is-source-delete-armed');
    }
    this._citationResetArms.delete(armKey);
  }

  /**
   * Parse selected text as a citation using the API
   * @param {Object} selectionData - Data about the selection
   * @param {string} selectionData.selectedText - The text user selected
   * @param {number} selectionData.pageNumber - Page number of the selection
   * @param {string} selectionData.pageText - Full text of the current page
   * @param {string|null} selectionData.prevPageText - Text of previous page (for context)
   * @param {string|null} selectionData.nextPageText - Text of next page (for context)
   * @param {Function} rerenderCallback - Optional callback to re-render the footnote list
   * @returns {Promise<Object|null>} The parsed citation or null if parsing failed
   */
  async addManualCitation(selectionData, rerenderCallback = null) {
    const { selectedText, pageNumber, pageText, prevPageText, nextPageText } = selectionData;
    
    if (!selectedText || !pageNumber) {
      this.platform?.setStatus?.('No text selected.');
      return null;
    }
    
    if (!this.currentDocumentId) {
      this.platform?.setStatus?.('No document loaded. Please load a PDF first.');
      return null;
    }
    
    this.platform?.setStatus?.('Parsing selected text as citation...');
    
    try {
      const authHeaders = await this.getAuthHeaders();
      const documentPath = this.platform?.getDocumentPath ? await this.platform.getDocumentPath() : null;
      const dataSource = window.footcheckDataSourceManager?.getSelectedDataSource?.() || 'internet';
      
      // Find heading for the page
      let headingId = null;
      for (const heading of (this.headings || [])) {
        if (heading.page <= pageNumber) {
          headingId = heading.id;
        }
      }
      
      // Use web-specific endpoint for web app, regular endpoint for desktop
      const endpoint = this.isWebApp 
        ? `${this.apiBase}/web/audit/parse_selection`
        : `${this.apiBase}/audit/parse_selection`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          selected_text: selectedText,
          page_number: pageNumber,
          page_text: pageText,
          prev_page_text: prevPageText,
          next_page_text: nextPageText,
          document_id: this.currentDocumentId,
          document_path: documentPath,
          data_source: dataSource,
          heading_id: headingId,
          client_id: this.clientId,  // For bridge caching in desktop app
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok || result.status === 'error') {
        const errMsg = result.error || 'Failed to parse citation';
        this.platform?.setStatus?.(`Error: ${errMsg}`);
        return null;
      }
      
      if (!result.citation) {
        this.platform?.setStatus?.('Selected text could not be parsed as a citation.');
        return null;
      }
      
      const citation = result.citation;
      console.log('[FootnotesManager] Parsed manual citation:', citation);
      
      // Create a footnote-like object for this manual citation
      const manualFootnote = {
        id: citation.id,
        reference_id: citation.id,
        text: citation.source?.raw || selectedText,
        context: citation.context || '',
        full_context: pageText,
        page_numbers: [pageNumber],
        heading_id: headingId,
        rich_text: null,
        is_inline: true,
        is_manual: true,
        inline_citation: citation,
        parsed_citations: [{
          ...citation,
          footnote_id: citation.id,
        }],
      };
      
      // Add to the items and footnotesCache
      this.items = [...(this.items || []), manualFootnote];
      this.footnotesCache = [...(this.footnotesCache || []), manualFootnote];
      
      // Register citation type for filtering
      this.registerCitationTypes([citation]);
      
      // Re-render the footnote list if callback provided
      if (rerenderCallback) {
        rerenderCallback();
      }
      
      this.platform?.setStatus?.(`Citation added: "${(citation.source?.raw || selectedText).substring(0, 50)}..."`);
      
      return citation;
    } catch (err) {
      console.error('[FootnotesManager] Error adding manual citation:', err);
      this.platform?.setStatus?.(`Error: ${err.message || err}`);
      return null;
    }
  }

  /**
   * Get the text of a page by page number
   * @param {number} pageNumber - The page number (1-indexed)
   * @returns {string|null} The page text or null if not found
   */
  getPageText(pageNumber) {
    if (!this.pages || !Array.isArray(this.pages)) return null;
    
    const page = this.pages.find(p => (p.page_number ?? this.pages.indexOf(p) + 1) === pageNumber);
    return page?.main_text || page?.markdown || null;
  }

  /**
   * Return every item that can appear in reports, including inline citations
   * that may live only in `items` after inline parsing.
   */
  getReportItems() {
    const merged = [];
    const seen = new Set();
    for (const list of [this.footnotesCache, this.items]) {
      if (!Array.isArray(list)) continue;
      for (const item of list) {
        if (!item) continue;
        const key = String(item.id ?? item.reference_id ?? merged.length);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
    }
    return merged;
  }

  /**
   * Generate HTML report from verified footnotes and pages
   * @param {string} pdfName - Name of the PDF file
   * @returns {Promise<Blob>} HTML file as blob
   */
  async generateHtmlReport(pdfName = "document.pdf") {
    return await generateHtmlReport({
      pages: this.pages,
      footnotes: this.getReportItems(),
      resultsCache: this.resultsCache,
      documentId: this.currentDocumentId,
      documentPath: this.state.documentPath,
      apiBase: this.apiBase,
      getAuthHeaders: () => this.getAuthHeaders(),
      pdfName,
    });
  }

  /**
   * Generate an annotated PDF report: the original PDF marked up with citation
   * highlights and popup comments. Requires the original PDF bytes to be loaded
   * (this.state.fileBytes), so it is unavailable for documents that were never
   * uploaded as bytes (e.g. some hosted Web App flows).
   * @param {string} pdfName - Name of the PDF file
   * @returns {Promise<Blob>} Annotated PDF file as blob
   */
  async generatePdfReport(pdfName = "document.pdf") {
    return await generatePdfReport({
      fileBytes: this.state.fileBytes,
      footnotes: this.getReportItems(),
      resultsCache: this.resultsCache,
      apiBase: this.apiBase,
      getAuthHeaders: () => this.getAuthHeaders(),
      pdfName,
    });
  }

  /** Whether an annotated-PDF report can be produced (original bytes present). */
  canGeneratePdfReport() {
    return Boolean(this.state?.fileBytes?.length && this.getReportItems().length);
  }
}
